import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { GiftDispatchManagementContent } from "../components/GiftDispatchManagementContent";
import { PublisherBalancesContent } from "../components/PublisherBalancesContent";

const API_ADMIN_PERFORMANCE = "/api/employee-performance-summary.php";
const API_GIFT_AWARD_LIST = "/api/gift-award-list.php";
const API_GIFT_AWARD_SAVE = "/api/gift-award-save.php";
const API_GIFT_AWARD_BULK_SAVE = "/api/gift-award-bulk-save.php";
const API_GIFT_AWARD_PUBLISH = "/api/gift-award-publish.php";
const API_GIFT_AWARD_UNPUBLISH = "/api/gift-award-unpublish.php";
const API_GIFT_AWARD_DELETE = "/api/gift-award-delete.php";
const API_GIFT_AWARD_IMPORT_PREVIEW = "/api/gift-award-import-preview.php";
const API_GIFT_AWARD_DELIVERY_UPDATE = "/api/gift-award-delivery-update.php";
const API_GIFT_MATRIX_GET = "/api/gift-matrix-get.php";
const API_GIFT_MATRIX_SAVE = "/api/gift-matrix-save.php";
const API_TIKTOK_REPORT_SAVE = "/api/tiktok-report-save.php";
const API_TIKTOK_REPORT_GET = "/api/tiktok-report-get.php";
const API_TIKTOK_REPORT_DELETE = "/api/tiktok-report-delete.php";
const API_GIFT_DISPATCH_LIST = "/api/gift-dispatch-list.php";
const API_GIFT_DISPATCH_SAVE = "/api/gift-dispatch-save.php";
const API_GIFT_AWARD_CANCEL = "/api/gift-award-cancel.php";
const API_GIFT_BALANCES_LIST = "/api/gift-balances-list.php";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

export function formatDuration(seconds) {
  const total = Math.max(0, safeNumber(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours} sa ${minutes} dk`;
}

export function toInputDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function getLatestUploadedPeriod() {
  try {
    const raw = localStorage.getItem("wolf_tiktok_activity_monthly_reports_v1");
    if (raw) {
      const parsed = JSON.parse(raw);
      const months = Object.keys(parsed).sort().reverse();
      if (months.length > 0) return months[0];
    }
  } catch {}
  return "2026-09";
}

export function monthRange(periodMonth, offset = 0) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  const now = new Date();
  const year = match ? Number(match[1]) : now.getFullYear();
  const monthIndex = (match ? Number(match[2]) - 1 : now.getMonth()) + offset;
  return {
    start: toInputDate(new Date(year, monthIndex, 1)),
    end: toInputDate(new Date(year, monthIndex + 1, 0)),
  };
}

export function periodLabel(periodMonth) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return periodMonth || "-";
  return `${MONTHS_TR[Number(match[2]) - 1] || match[2]} ${match[1]}`;
}

export const formatGiftPeriodLabel = periodLabel;

export function downloadExcel(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = ["Yayıncı Adı", "Kullanıcı Adı", "Hidden ID", "Kazanılan Ödül", "Yayın Durumu", "Elmas", "Yayın Süresi (dk)", "Hedef Durumu"];
  const csvRows = [headers.join("\t")];
  rows.forEach(r => {
    csvRows.push([
      r.publisherName || "",
      r.publisherUsername || "",
      r.publisherHiddenId || "",
      r.giftAwardTitle || "",
      r.publishStatus || "",
      r.diamondsTotal || 0,
      r.durationMinutes || 0,
      r.isEligible ? "Hak Kazandı" : "Beklemede"
    ].join("\t"));
  });
  const blob = new Blob(["\ufeff" + csvRows.join("\n")], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".xls") ? filename : `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(url);
}

export function getHiddenId(item) {
  return String(item?.hiddenId || item?.hidden_id || item?.hiddenID || item?.hiddenid || "").trim();
}

export function deduplicatePublishersByHiddenId(publishers = []) {
  if (!Array.isArray(publishers) || publishers.length <= 1) {
    return publishers || [];
  }

  const seenHiddenIds = new Map();
  const noHiddenIdList = [];

  publishers.forEach((pub, index) => {
    const rawHiddenId = getHiddenId(pub);

    if (!rawHiddenId || rawHiddenId === "-" || rawHiddenId === "0" || rawHiddenId.toLowerCase() === "null") {
      noHiddenIdList.push(pub);
      return;
    }

    const key = rawHiddenId.toLowerCase();
    const existing = seenHiddenIds.get(key);

    if (!existing) {
      seenHiddenIds.set(key, { ...pub, _originalIndex: index });
    } else {
      const existingId = Number(existing.id || existing.publisherId || existing.userId) || 0;
      const currentId = Number(pub.id || pub.publisherId || pub.userId) || 0;

      const existingCreatedAt = existing.createdAt || existing.raw?.createdAt || existing.raw?.created_at || "";
      const currentCreatedAt = pub.createdAt || pub.raw?.createdAt || pub.raw?.created_at || "";

      let isCurrentNewer = false;

      if (currentId > 0 && existingId > 0 && currentId !== existingId) {
        isCurrentNewer = currentId > existingId;
      } else if (existingCreatedAt && currentCreatedAt && existingCreatedAt !== currentCreatedAt) {
        const timeExisting = new Date(String(existingCreatedAt).replace(" ", "T")).getTime() || 0;
        const timeCurrent = new Date(String(currentCreatedAt).replace(" ", "T")).getTime() || 0;
        isCurrentNewer = timeCurrent >= timeExisting;
      } else {
        isCurrentNewer = true;
      }

      if (isCurrentNewer) {
        seenHiddenIds.set(key, { ...pub, _originalIndex: index });
      }
    }
  });

  const deduplicated = Array.from(seenHiddenIds.values()).map((item) => {
    const { _originalIndex, ...rest } = item;
    return rest;
  });

  return [...deduplicated, ...noHiddenIdList];
}

export function normalizeUsername(value) {
  return String(value || "").trim().replace(/^@/, "").replace(/\s+/g, "").toLocaleLowerCase("tr-TR");
}

/* ==========================================================================
   TİKTOK AKTİFLİK VE YÜKSELME AYLIK HEDİYE HAKEDİŞ HESAPLAMA MOTORU
   - Aktiflik ve Yükselme sayfalarında ortak bulunan yayıncılar eşleştirilir.
   - Taban Tutar: Yükselme sayfasındaki "Tahminî bonus katkısı" ($)
   - Çarpan: Aktiflik seviyesi 4 ise %30 (0.30), 5 ve üzeri ise %50 (0.50)
   - < 4. Seviye veya eşleşmeyenler: %0 (0.00)
   ========================================================================== */
export function parseAktiflikSheet(worksheet) {
  if (!worksheet) return new Map();
  const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
  const map = new Map();
  let currentUser = null;
  let currentData = null;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.length === 0) continue;
    const colA = String(r[0] != null ? r[0] : "").trim();
    if (!colA || colA.includes("İçerik Üreticisi") || colA.includes("İçerik üreticisi")) continue;

    const levelMatch = colA.match(/(\d+)\.\s*Seviye/i) || colA.match(/Seviye\s*(\d+)/i);
    if (levelMatch && currentUser && currentData) {
      currentData.level = parseInt(levelMatch[1], 10);
      currentData.levelRaw = colA;
      continue;
    }

    if (!levelMatch && !colA.startsWith("Kademe") && !colA.includes("ayki") && !colA.includes("En yüksek")) {
      currentUser = colA.toLowerCase().replace(/^@+/, "").replace(/\s+/g, "").trim();
      let bonusRaw = String(r[1] != null ? r[1] : "").trim();
      let bonusVal = 0;
      if (bonusRaw) {
        let numStr = bonusRaw.replace("$", "").replace(/\s+/g, "").replace(/\./g, "").replace(",", ".");
        bonusVal = parseFloat(numStr) || 0;
      }
      let rate = r[2] != null ? (typeof r[2] === "number" ? r[2] : parseFloat(String(r[2]).replace("%", "").replace(",", ".")) / (String(r[2]).includes("%") ? 100 : 1)) : 0;
      let diamonds = r[3] != null ? (typeof r[3] === "number" ? r[3] : parseFloat(String(r[3]).replace(/\./g, "")) || 0) : 0;

      currentData = {
        username: currentUser,
        originalUsername: colA,
        bonusRaw,
        bonusVal,
        rate: isNaN(rate) ? 0 : rate,
        diamonds: isNaN(diamonds) ? 0 : diamonds,
        liveDays: r[4] || "",
        liveHours: r[5] || "",
        level: 0,
        levelRaw: ""
      };
      map.set(currentUser, currentData);
    }
  }
  return map;
}

export function parseYukselmeSheet(worksheet) {
  if (!worksheet) return new Map();
  const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
  const map = new Map();
  let currentUser = null;
  let currentData = null;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.length === 0) continue;
    const colA = String(r[0] != null ? r[0] : "").trim();
    if (!colA || colA.includes("İçerik Üreticisi") || colA.includes("İçerik üreticisi")) continue;

    const kademeMatch = colA.match(/Kademe\s*(\d+)/i);
    if (kademeMatch && currentUser && currentData) {
      currentData.kademe = parseInt(kademeMatch[1], 10);
      currentData.kademeRaw = colA;
      continue;
    }

    if (!kademeMatch && !colA.includes("Seviye") && !colA.includes("ayki") && !colA.includes("Yükseldi")) {
      currentUser = colA.toLowerCase().replace(/^@+/, "").replace(/\s+/g, "").trim();
      let bonusRaw = String(r[1] != null ? r[1] : "").trim();
      let bonusVal = 0;
      if (bonusRaw) {
        let numStr = bonusRaw.replace("$", "").replace(/\s+/g, "").replace(/\./g, "").replace(",", ".");
        bonusVal = parseFloat(numStr) || 0;
      }
      let rate = r[2] != null ? (typeof r[2] === "number" ? r[2] : parseFloat(String(r[2]).replace("%", "").replace(",", ".")) / (String(r[2]).includes("%") ? 100 : 1)) : 0;

      currentData = {
        username: currentUser,
        originalUsername: colA,
        bonusRaw,
        bonusVal,
        rate: isNaN(rate) ? 0 : rate,
        progress: r[3] || "",
        kademe: 0,
        kademeRaw: ""
      };
      map.set(currentUser, currentData);
    }
  }
  return map;
}

export function buildTikTokReportStats(rows = []) {
  let totalHakedis = 0;
  let eligibleCount = 0;
  let level5Count = 0;
  let level4Count = 0;
  let unmatchedCount = 0;
  let manualCount = 0;

  (rows || []).forEach((r) => {
    const finalAmount = r.customHakEdis != null ? Number(r.customHakEdis) : Number(r.hakEdis || 0);
    if (r.isEligible || r.isManual || finalAmount > 0) {
      totalHakedis += finalAmount;
      eligibleCount++;
    }
    if (r.aktiflikLevel >= 5 && (r.isEligible || finalAmount > 0)) level5Count++;
    else if (r.aktiflikLevel === 4 && (r.isEligible || finalAmount > 0)) level4Count++;
    else if (!r.isEligible && !r.isManual && finalAmount <= 0) unmatchedCount++;
    if (r.isManual || r.customHakEdis != null) manualCount++;
  });

  return {
    totalHakedis: Math.round(totalHakedis * 100) / 100,
    eligibleCount,
    level5Count,
    level4Count,
    unmatchedCount,
    manualCount,
    totalRows: (rows || []).length
  };
}

export function parseTikTokActivityWorkbook(workbook, periodMonth = defaultMonth()) {
  if (!workbook || !workbook.SheetNames || !workbook.SheetNames.length) {
    return { rows: [], stats: { totalHakedis: 0, eligibleCount: 0, level5Count: 0, level4Count: 0, unmatchedCount: 0, manualCount: 0, totalRows: 0 } };
  }

  const sheetNames = workbook.SheetNames;
  const findSheet = (...names) => {
    for (const n of names) {
      const found = sheetNames.find((s) => s.toLowerCase().trim() === n.toLowerCase().trim());
      if (found) return workbook.Sheets[found];
    }
    return null;
  };

  const aktiflikSheet = findSheet("Aktiflik", "aktiflik", "Activity", "activity", "Sheet1");
  const yukselmeSheet = findSheet("yükselme", "yukselme", "Yükselme", "Yukselme", "Progression", "Sheet2");

  if (!aktiflikSheet || !yukselmeSheet) {
    const firstSheet = workbook.Sheets[sheetNames[0]];
    const jsonRows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
    const rows = jsonRows.map((r, idx) => {
      const u = String(r["Kullanıcı Adı"] || r.username || r["İçerik Üreticisi"] || r.User || "").replace(/^@/, "").trim();
      const bonus = parseFloat(String(r["Tahminî bonus katkısı"] || r.bonus || r["Bonus"] || 0).replace("$", "").replace(",", ".")) || 0;
      const level = parseInt(r["Aktiflik Seviyesi"] || r.level || 5, 10) || 5;
      const mult = level === 4 ? 0.30 : (level >= 5 ? 0.50 : 0);
      const hakEdis = Math.round(bonus * mult * 100) / 100;
      return {
        id: `row_${idx}_${u}`,
        username: u.toLowerCase(),
        originalUsername: u,
        aktiflikLevel: level,
        aktiflikLevelRaw: `${level}. Seviye`,
        aktiflikRate: 0.025,
        aktiflikDiamonds: 0,
        yukselmeKademe: 0,
        yukselmeKademeRaw: "-",
        yukselmeRate: 0.06,
        yukselmeBonus: bonus,
        multiplier: mult,
        hakEdis,
        customHakEdis: null,
        isEligible: mult > 0 && hakEdis > 0,
        status: mult > 0 ? "ELIGIBLE" : "NOT_ELIGIBLE_LEVEL",
        statusLabel: mult > 0 ? `Hak Kazandı (%${(mult * 100).toFixed(0)})` : "Seviye Yetersiz",
        periodMonth
      };
    }).filter((r) => Boolean(r.username));
    return { rows, stats: buildTikTokReportStats(rows) };
  }

  const aktiflikMap = parseAktiflikSheet(aktiflikSheet);
  const yukselmeMap = parseYukselmeSheet(yukselmeSheet);

  const allKeys = new Set([...aktiflikMap.keys(), ...yukselmeMap.keys()]);
  const rows = [];

  for (const u of allKeys) {
    const a = aktiflikMap.get(u);
    const y = yukselmeMap.get(u);

    const originalUsername = (y && y.originalUsername) || (a && a.originalUsername) || u;
    const inAktiflik = Boolean(a);
    const inYukselme = Boolean(y);

    const aktiflikLevel = a ? a.level : 0;
    const aktiflikLevelRaw = a ? (a.levelRaw || (a.level ? `${a.level}. Seviye` : "-")) : "-";
    const aktiflikRate = a ? a.rate : 0;
    const aktiflikDiamonds = a ? a.diamonds : 0;
    const aktiflikLiveDays = a ? a.liveDays : "";
    const aktiflikLiveHours = a ? a.liveHours : "";

    const yukselmeKademe = y ? y.kademe : 0;
    const yukselmeKademeRaw = y ? (y.kademeRaw || (y.kademe ? `Kademe ${y.kademe}` : "-")) : "-";
    const yukselmeRate = y ? y.rate : 0;
    const yukselmeBonus = y ? y.bonusVal : 0;
    const yukselmeBonusRaw = y ? y.bonusRaw : "-";
    const yukselmeProgress = y ? y.progress : "";

    let multiplier = 0;
    let status = "UNMATCHED";
    let statusLabel = "";

    if (inAktiflik && inYukselme) {
      if (aktiflikLevel === 4) {
        multiplier = 0.30;
        status = "ELIGIBLE";
        statusLabel = "Hak Kazandı (%30 - 4. Seviye)";
      } else if (aktiflikLevel >= 5) {
        multiplier = 0.50;
        status = "ELIGIBLE";
        statusLabel = "Hak Kazandı (%50 - 5. Seviye)";
      } else {
        multiplier = 0;
        status = "NOT_ELIGIBLE_LEVEL";
        statusLabel = `Seviye Yetersiz (${aktiflikLevel || 0}. Seviye)`;
      }
    } else if (inYukselme && !inAktiflik) {
      status = "ONLY_YUKSELME";
      statusLabel = "Sadece Yükselme Sayfasında Var (Aktiflik Yok)";
    } else if (inAktiflik && !inYukselme) {
      status = "ONLY_AKTIFLIK";
      statusLabel = "Sadece Aktiflik Sayfasında Var (Yükselme Yok)";
    }

    const hakEdis = Math.round((yukselmeBonus * multiplier) * 100) / 100;

    rows.push({
      id: `row_${u}`,
      username: u,
      originalUsername,
      inAktiflik,
      inYukselme,
      aktiflikLevel,
      aktiflikLevelRaw,
      aktiflikRate,
      aktiflikDiamonds,
      aktiflikLiveDays,
      aktiflikLiveHours,
      yukselmeKademe,
      yukselmeKademeRaw,
      yukselmeRate,
      yukselmeBonus,
      yukselmeBonusRaw,
      yukselmeProgress,
      multiplier,
      hakEdis,
      customHakEdis: null,
      isEligible: status === "ELIGIBLE" && hakEdis > 0,
      status,
      statusLabel,
      adminNote: "",
      periodMonth
    });
  }

  rows.sort((a, b) => {
    if (b.isEligible !== a.isEligible) return b.isEligible ? 1 : -1;
    const amountA = a.customHakEdis != null ? Number(a.customHakEdis) : Number(a.hakEdis || 0);
    const amountB = b.customHakEdis != null ? Number(b.customHakEdis) : Number(b.hakEdis || 0);
    if (amountB !== amountA) return amountB - amountA;
    return a.username.localeCompare(b.username);
  });

  return { rows, stats: buildTikTokReportStats(rows) };
}

/* ==========================================================================
   JETON & ELMAS DÖNÜŞÜM VE BİÇİMLENDİRME MOTORU
   - Admin Görünümü: Tam değer (Örn: "1.223 Elmas", "1.279 Elmas", "93.283 Elmas")
   - Yayıncı Görünümü: K biçimi (Örn: 1223 -> "1,2 K Elmas", 1279 -> "1,2 K Elmas", 2000 -> "2 K Elmas")
   Varsayılan Kur: 1 Jeton / Elmas = 0.02 $
   ========================================================================== */
export function dollarsToJeton(dollars, rate = 0.02) {
  const d = Number(dollars) || 0;
  const r = Number(rate) > 0 ? Number(rate) : 0.02;
  return Math.round(d / r);
}

// 📱 YAYINCI FORMATI: 1223 -> "1,2 K Elmas", 1279 -> "1,2 K Elmas", 2000 -> "2 K Elmas"
export function formatPublisherElmas(coins) {
  const num = Math.round(Number(coins) || 0);
  if (num <= 0) return "0 Elmas";
  if (num < 1000) return `${num} Elmas`;

  const flooredTenth = Math.floor(num / 100) / 10;
  const formatted = (flooredTenth % 1 === 0)
    ? `${flooredTenth} K`
    : `${flooredTenth.toFixed(1).replace(".", ",")} K`;

  return `${formatted} Elmas`;
}

// 👑 ADMİN TAM DEĞER FORMATI: "1.223 Elmas", "1.279 Elmas", "93.283 Elmas"
export function formatExactElmas(coins) {
  const num = Math.round(Number(coins) || 0);
  return `${num.toLocaleString("tr-TR")} Elmas`;
}

export function formatJetonKStyle(coins) {
  return formatPublisherElmas(coins);
}

export function formatDollarsAsJeton(dollars, rate = 0.02) {
  const coins = dollarsToJeton(dollars, rate);
  return formatPublisherElmas(coins);
}

export function exportTikTokActivityCalculationToExcel(rows = [], periodMonth = defaultMonth(), stats = {}, jetonRate = 0.02) {
  if (!rows || !rows.length) return;
  const currentRate = Number(jetonRate) > 0 ? Number(jetonRate) : 0.02;
  const data = rows.map((r, idx) => {
    const finalAmount = r.customHakEdis != null ? Number(r.customHakEdis) : Number(r.hakEdis || 0);
    const coins = dollarsToJeton(finalAmount, currentRate);
    const exactLabel = formatExactElmas(coins);
    const pubLabel = formatPublisherElmas(coins);

    return {
      "Sıra": idx + 1,
      "Yayıncı Kullanıcı Adı": `@${r.originalUsername || r.username}`,
      "Aktiflik Seviyesi": r.aktiflikLevel ? `${r.aktiflikLevel}. Seviye` : (r.inAktiflik ? "Var (Seviye < 4)" : "Aktiflikte Yok"),
      "Aktiflik Oranı": r.aktiflikRate ? `%${(r.aktiflikRate * 100).toFixed(2)}` : "-",
      "Aktiflik Elmas": r.aktiflikDiamonds ? Number(r.aktiflikDiamonds).toLocaleString("tr-TR") : "-",
      "Yükselme Kademe": r.yukselmeKademe ? `Kademe ${r.yukselmeKademe}` : (r.inYukselme ? "Var" : "Yükselmede Yok"),
      "Yükselme Oranı": r.yukselmeRate ? `%${(r.yukselmeRate * 100).toFixed(2)}` : "-",
      "Tahminî Yükselme Bonusu ($)": r.yukselmeBonus ? `${Number(r.yukselmeBonus).toFixed(2)} $` : "0.00 $",
      "Uygulanan Çarpan (%)": r.multiplier ? `%${(r.multiplier * 100).toFixed(0)}` : (r.isManual ? "Özel" : "%0"),
      "Hesaplanan Net Hakediş ($)": `${finalAmount.toFixed(2)} $`,
      "Net Elmas Hakedişi (Admin Tam Değer)": exactLabel,
      "Yayıncı Paneli Görünümü": pubLabel,
      "Durum": r.statusLabel || (r.isEligible ? "Hak Kazandı" : "Hak Kazanamadı"),
      "Dönem": periodMonth || defaultMonth(),
      "Yönetici Notu": r.adminNote || (r.isManual ? "Manuel Özel Prim" : "")
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Hakediş Hesaplaması");
  XLSX.writeFile(wb, `TikTok_Hediye_Hakedis_${periodMonth || "donem"}.xlsx`);
}

export function generateWhatsAppGiftReport(rows = [], periodMonth = defaultMonth(), stats = {}, jetonRate = 0.02) {
  const currentRate = Number(jetonRate) > 0 ? Number(jetonRate) : 0.02;
  const eligibleRows = rows.filter((r) => r.isEligible || (r.customHakEdis != null && Number(r.customHakEdis) > 0) || r.isManual);
  const totalCoins = dollarsToJeton(stats.totalHakedis || 0, currentRate);
  let text = `🐺 *WOLF AJANS - ${periodLabel(periodMonth).toUpperCase()} HEDİYE VE PRİM HAKEDİŞ LİSTESİ*\n\n`;
  text += `📊 *Genel Özet:*\n`;
  text += `• Toplam Dağıtılacak Hediye / Prim: *$${Number(stats.totalHakedis || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}* (💎 *${formatExactElmas(totalCoins)}* / Yayıncı: *${formatPublisherElmas(totalCoins)}*)\n`;
  text += `• 1 Jeton / Elmas Birim Kuru: *$${currentRate}* (1 $ = ${(1 / currentRate).toFixed(0)} Jeton)\n`;
  text += `• Hak Kazanan Yayıncı: *${stats.eligibleCount || eligibleRows.length} Kişi*\n`;
  text += `• 5. Seviye (%50 Çarpan): *${stats.level5Count || 0} Kişi*\n`;
  text += `• 4. Seviye (%30 Çarpan): *${stats.level4Count || 0} Kişi*\n\n`;
  text += `🏆 *YAYINCI HAKEDİŞ LİSTESİ:*\n`;
  text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;

  eligibleRows.forEach((r, idx) => {
    const finalAmount = r.customHakEdis != null ? Number(r.customHakEdis) : Number(r.hakEdis || 0);
    const coins = dollarsToJeton(finalAmount, currentRate);
    const exactCoins = formatExactElmas(coins);
    const pubK = formatPublisherElmas(coins);
    const levelStr = r.aktiflikLevel ? `${r.aktiflikLevel}. Seviye (%${(r.multiplier * 100).toFixed(0)})` : (r.isManual ? "Özel Prim" : "");
    text += `${idx + 1}. *@${r.originalUsername || r.username}* ➔ 💎 *${exactCoins}* (Yayıncıda: *${pubK}*) - $${finalAmount.toFixed(2)} ${levelStr ? `(${levelStr})` : ""}\n`;
  });

  text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  text += `📌 *Hesaplama Usulü:* Aktiflik Seviyesi 4 olan yayıncılara yükselme bonusunun %30'u, 5 ve üzeri olanlara %50'si yansıtılmıştır. Tebrikler! 🎉`;
  return text;
}

/* ==========================================================================
   KADEME HESAPLAMA (Sadece Aylık Toplam Elmasa Göre)
   K1: 0 – 39.999 | K2: 40.000 – 79.999 | K3: 80.000 – 149.999 | K4: 150.000 – 299.999 | K5: 300.000 – 499.999
   K6: 500.000 – 799.999 | K7: 800.000 – 1.199.999 | K8: 1.200.000 – 1.599.999 | K9: 1.600.000 – 2.999.999 | K10: 3.000.000+
   ========================================================================== */
export function getTierNo(diamonds) {
  const value = safeNumber(diamonds);
  if (value >= 3000000) return 10;
  if (value >= 1600000) return 9;
  if (value >= 1200000) return 8;
  if (value >= 800000) return 7;
  if (value >= 500000) return 6;
  if (value >= 300000) return 5;
  if (value >= 150000) return 4;
  if (value >= 80000) return 3;
  if (value >= 40000) return 2;
  return 1;
}

/* ==========================================================================
   DİNAMİK PHP HEDİYE MATRİSİ ÇÖZÜMLEME
   ========================================================================== */
export function resolveTierReward(matrix = [], currentTierNo = 1, previousTierNo = null, isFirstMonth = false) {
  if (!Array.isArray(matrix) || matrix.length === 0) {
    return "-";
  }

  const tierRow = matrix.find((m) => Number(m.tierNo) === Number(currentTierNo));
  if (!tierRow) return "-";

  // Kademe Düşüşü (currentTier < previousTier)
  if (previousTierNo && Number(previousTierNo) > 0 && Number(currentTierNo) < Number(previousTierNo)) {
    return "-";
  }

  // Kademe Koruma (currentTier === previousTier)
  if (previousTierNo && Number(previousTierNo) > 0 && Number(currentTierNo) === Number(previousTierNo)) {
    return tierRow.protectReward || "-";
  }

  // İlk Ay (1. Ay) veya önceki kademesi olmayan yeni yayıncı -> +1 yükselme kabul edilir
  if (isFirstMonth || !previousTierNo || Number(previousTierNo) === 0) {
    return tierRow.upgradeSingleReward || "-";
  }

  // Kademe Yükselme
  const diff = Number(currentTierNo) - Number(previousTierNo);
  if (diff >= 2) {
    return tierRow.upgradePlusReward || tierRow.upgradeSingleReward || "-";
  } else if (diff === 1) {
    return tierRow.upgradeSingleReward || "-";
  }

  return tierRow.upgradeSingleReward || "-";
}

export function tierChange(previousTier, currentTier) {
  const diff = safeNumber(currentTier) - safeNumber(previousTier);
  if (diff > 0) return `${diff} Kademe Arttı`;
  if (diff < 0) return `${Math.abs(diff)} Kademe Düştü`;
  return "Kademe Korundu";
}

export function tierChangeGroup(text) {
  const value = String(text || "").toLocaleLowerCase("tr-TR");
  if (value.includes("arttı")) return "up";
  if (value.includes("düştü")) return "down";
  return "same";
}

export function tierChangeClass(text) {
  const group = tierChangeGroup(text);
  if (group === "up") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (group === "down") return "border-red-500/30 bg-red-500/10 text-red-200";
  return "border-white/15 bg-white/[0.04] text-white/65";
}

/* ==========================================================================
   MERKEZİ STANDART ÖDÜL HAK EDİŞ MOTORU
   Şartlar (Tüm Kademeler K1-K10 İçin Aynı):
   1. Aktif Gün >= 22 gün (farklı takvim günleri)
   2. Yayın Saati >= 80 saat
   3. Toplam Elmas >= 40.000 elmas
   3/3 -> AUTO_ELIGIBLE (Otomatik Hak Kazandı)
   2/3 -> MANUAL_REVIEW (Manuel Değerlendirme Havuzu - eksik şart açıkça belirtilir)
   <=1/3 -> NOT_ELIGIBLE (Hak Kazanmadı)
   ========================================================================== */
export function calculateRewardEligibility({
  activeDays = 0,
  liveHours = 0,
  monthlyDiamonds = 0,
  previousTier = null,
  isFirstMonth = false,
  matrix = [],
}) {
  const safeDays = safeNumber(activeDays);
  const safeHours = safeNumber(liveHours);
  const safeDiamonds = safeNumber(monthlyDiamonds);

  const dayMet = safeDays >= 22;
  const hourMet = safeHours >= 80;
  const diamondMet = safeDiamonds >= 40000;

  const metCount = (dayMet ? 1 : 0) + (hourMet ? 1 : 0) + (diamondMet ? 1 : 0);

  let status = "NOT_ELIGIBLE";
  if (metCount === 3) {
    status = "AUTO_ELIGIBLE";
  } else if (metCount === 2) {
    status = "MANUAL_REVIEW";
  }

  const missingConditions = [];
  if (!dayMet) {
    const diff = 22 - safeDays;
    missingConditions.push({
      type: "day",
      label: `${diff} gün eksik`,
      detail: `${safeDays} / 22 Gün`,
      current: safeDays,
      target: 22,
      diff,
    });
  }
  if (!hourMet) {
    const diff = Math.round((80 - safeHours) * 10) / 10;
    missingConditions.push({
      type: "hour",
      label: `${diff} saat eksik`,
      detail: `${safeHours} / 80 Saat`,
      current: safeHours,
      target: 80,
      diff,
    });
  }
  if (!diamondMet) {
    const diff = 40000 - safeDiamonds;
    missingConditions.push({
      type: "diamond",
      label: `${diff.toLocaleString("tr-TR")} elmas eksik`,
      detail: `${safeDiamonds.toLocaleString("tr-TR")} / 40.000 Elmas`,
      current: safeDiamonds,
      target: 40000,
      diff,
    });
  }

  const currentTier = getTierNo(safeDiamonds);
  const rewardMode = isFirstMonth || previousTier === null
    ? "first_month"
    : (currentTier > previousTier ? "upgrade" : (currentTier === previousTier ? "protect" : "drop"));

  const defaultReward = resolveTierReward(matrix, currentTier, previousTier, isFirstMonth);

  return {
    status,
    metCount,
    dayMet,
    hourMet,
    diamondMet,
    missingConditions,
    isAutoEligible: status === "AUTO_ELIGIBLE",
    isManualReview: status === "MANUAL_REVIEW",
    isNotEligible: status === "NOT_ELIGIBLE",
    rewardMode,
    currentTier,
    defaultReward,
  };
}

/* ==========================================================================
   LOCAL EVALUATION & MONTHLY TIKTOK REPORT STORAGE (Aylık Hakediş Saklama Motoru)
   ========================================================================== */
const STORAGE_KEY_EVALUATIONS = "wolf_gift_manual_evaluations_v1";
const STORAGE_KEY_MATRIX_CACHE = "wolf_gift_matrix_cache_v1";
export const STORAGE_KEY_TIKTOK_MONTHLY_REPORTS = "wolf_tiktok_activity_monthly_reports_v1";

export function loadMonthlyTikTokReports() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TIKTOK_MONTHLY_REPORTS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    let changed = false;
    // Sahte/senkronizasyon kaynaklı bozuk tek kişilik veya boş raporları temizle
    Object.keys(parsed).forEach((k) => {
      const rep = parsed[k];
      if (rep && (rep.updatedBy === "server_awards_sync" || (rep.fileName && String(rep.fileName).startsWith("Yayınlanan_Ödüller_")))) {
        delete parsed[k];
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem(STORAGE_KEY_TIKTOK_MONTHLY_REPORTS, JSON.stringify(parsed));
    }
    return parsed;
  } catch {
    return {};
  }
}

export async function syncMonthlyTikTokReportToServer(periodMonth, reportData = {}) {
  if (!periodMonth) return;
  try {
    await requestJson(API_TIKTOK_REPORT_SAVE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        periodMonth,
        report: reportData
      })
    });
  } catch (err) {
    console.warn("Aylık TikTok raporu sunucuya aktarılamadı (çevrimdışı veya hata):", err);
  }
}

export async function deleteMonthlyTikTokReportFromServer(periodMonth) {
  if (!periodMonth) return;
  try {
    await requestJson(API_TIKTOK_REPORT_DELETE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ periodMonth })
    });
  } catch (err) {
    console.warn("Aylık TikTok raporu sunucudan silinemedi:", err);
  }
}

export async function fetchMonthlyTikTokReportsFromServer() {
  try {
    const res = await requestJson(API_TIKTOK_REPORT_GET, { method: "GET" });
    if (res && res.success && res.reports && typeof res.reports === "object") {
      const serverReports = res.reports;
      const local = loadMonthlyTikTokReports();
      let hasChanges = false;
      const merged = { ...local };

      Object.entries(serverReports).forEach(([month, report]) => {
        if (report && Array.isArray(report.rows) && report.rows.length > 0) {
          const localRep = local[month];
          if (!localRep || (report.rows.length >= (localRep.rows?.length || 0))) {
            merged[month] = report;
            hasChanges = true;
          }
        }
      });

      // Eğer tarayıcıda olan rapor sunucuda henüz yoksa arka planda sunucuya da gönder
      Object.entries(local).forEach(([month, report]) => {
        if (!serverReports[month] && report && Array.isArray(report.rows) && report.rows.length > 0) {
          syncMonthlyTikTokReportToServer(month, report);
        }
      });

      if (hasChanges && typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY_TIKTOK_MONTHLY_REPORTS, JSON.stringify(merged));
      }
      return { reports: merged };
    }
  } catch (err) {
    console.warn("Sunucudan kayıtlı raporlar çekilemedi:", err);
  }
  return { reports: loadMonthlyTikTokReports() };
}

export function saveMonthlyTikTokReport(periodMonth, reportData = {}) {
  if (typeof window === "undefined" || !periodMonth) return;
  try {
    const all = loadMonthlyTikTokReports();
    const updated = {
      ...(all[periodMonth] || {}),
      ...reportData,
      periodMonth,
      updatedAt: new Date().toISOString(),
    };
    all[periodMonth] = updated;
    localStorage.setItem(STORAGE_KEY_TIKTOK_MONTHLY_REPORTS, JSON.stringify(all));

    // Sunucu veritabanına ve dosya sistemine kaydet (tüm admin bilgisayarlarında görülmesi için)
    syncMonthlyTikTokReportToServer(periodMonth, updated);
  } catch (err) {
    console.error("Aylık TikTok raporu kaydedilemedi:", err);
  }
}

export function deleteMonthlyTikTokReport(periodMonth) {
  if (typeof window === "undefined" || !periodMonth) return;
  try {
    const all = loadMonthlyTikTokReports();
    delete all[periodMonth];
    localStorage.setItem(STORAGE_KEY_TIKTOK_MONTHLY_REPORTS, JSON.stringify(all));

    // Sunucudan da sil
    deleteMonthlyTikTokReportFromServer(periodMonth);
  } catch (err) {
    console.error("Aylık TikTok raporu silinemedi:", err);
  }
}

export function getSavedTikTokReportMonths() {
  const all = loadMonthlyTikTokReports();
  return Object.keys(all).sort().reverse();
}

export function loadStoredEvaluations() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY_EVALUATIONS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveStoredEvaluation(periodMonth, identifier, payload) {
  if (typeof window === "undefined" || !periodMonth || !identifier) return;
  try {
    const key = `${periodMonth}:${String(identifier).trim().toLowerCase()}`;
    const all = loadStoredEvaluations();
    all[key] = {
      ...all[key],
      ...payload,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY_EVALUATIONS, JSON.stringify(all));
  } catch (err) {
    console.error("Evaluation kaydedilemedi:", err);
  }
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, options);
  const raw = await response.text();
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  const trimmed = String(raw || "").trim();

  let data;
  try {
    data = trimmed ? JSON.parse(trimmed) : {};
  } catch {
    const looksLikeHtml =
      contentType.includes("text/html") ||
      /^<!doctype\s+html/i.test(trimmed) ||
      /^<html/i.test(trimmed);

    if (looksLikeHtml) {
      throw new Error(
        `Hediye API servisi JSON yerine web sayfası döndürdü. ${url} dosyasının sunucuda bulunduğunu kontrol edin.`
      );
    }

    throw new Error(`Hediye API servisi geçerli JSON döndürmedi. ${url}`);
  }

  if (!response.ok || data?.success === false) {
    throw new Error(
      data?.message ||
        data?.error ||
        `İşlem başarısız. HTTP ${response.status} (${url})`
    );
  }

  return data;
}

async function fetchPerformance(hiddenIds, range) {
  const ids = [...new Set((hiddenIds || []).filter(Boolean))];
  if (!ids.length) return { publishers: [], publisherDaily: [] };
  const aggregate = { publishers: [], publisherDaily: [] };
  const chunks = [];
  for (let i = 0; i < ids.length; i += 40) chunks.push(ids.slice(i, i + 40));

  for (const chunk of chunks) {
    const url = `${API_ADMIN_PERFORMANCE}?hiddenIds=${encodeURIComponent(chunk.join(","))}&startDate=${range.start}&endDate=${range.end}`;
    try {
      const result = await requestJson(url, { cache: "no-store" });
      (result.publishers || []).forEach((row) => aggregate.publishers.push(row));
      (result.publisherDaily || result.dailyByPublisher || []).forEach((row) => aggregate.publisherDaily.push(row));

      if (chunk.length > 1 && !(result.publisherDaily || result.dailyByPublisher)) {
        for (const hiddenId of chunk) {
          const singleUrl = `${API_ADMIN_PERFORMANCE}?hiddenIds=${encodeURIComponent(hiddenId)}&startDate=${range.start}&endDate=${range.end}`;
          try {
            const single = await requestJson(singleUrl, { cache: "no-store" });
            const daily = single.publisherDaily || (single.daily || []).map((item) => ({ ...item, hiddenId }));
            daily.forEach((row) => aggregate.publisherDaily.push(row));
          } catch {
            // Devam et
          }
        }
      }
    } catch {
      // API yanıt vermezse devam et
    }
  }
  return aggregate;
}

function perfRow(performance, hiddenId) {
  return (performance?.publishers || []).find((item) => String(getHiddenId(item)) === String(hiddenId)) || {};
}

function activeDays(performance, hiddenId) {
  const dates = (performance?.publisherDaily || [])
    .filter((item) => String(getHiddenId(item)) === String(hiddenId))
    .filter((item) => safeNumber(item.liveCount || item.live_count) > 0 || safeNumber(item.liveSeconds || item.live_seconds) > 0)
    .map((item) => String(item.report_date || item.reportDate || item.date || "").slice(0, 10))
    .filter(Boolean);
  return new Set(dates).size;
}

function buildActivityRows(publishers, managers, current, previous, storedEvals = {}, matrix = []) {
  const managerMap = {};
  (managers || []).forEach((manager) => {
    if (manager?.username) managerMap[String(manager.username)] = manager.fullName || manager.username;
  });

  const uniquePublishers = deduplicatePublishersByHiddenId(publishers || []);

  return uniquePublishers.map((publisher) => {
    const hiddenId = getHiddenId(publisher);
    const currentPerf = perfRow(current, hiddenId);
    const previousPerf = perfRow(previous, hiddenId);
    const diamonds = safeNumber(currentPerf.totalDiamonds || currentPerf.total_diamonds || currentPerf.diamonds);
    const oldDiamonds = safeNumber(previousPerf.totalDiamonds || previousPerf.total_diamonds || previousPerf.diamonds);
    const liveCount = safeNumber(currentPerf.totalLiveCount || currentPerf.total_live_count || currentPerf.liveCount);
    const liveSeconds = safeNumber(currentPerf.totalLiveSeconds || currentPerf.total_live_seconds || currentPerf.liveSeconds);
    const liveHours = Math.round((liveSeconds / 3600) * 10) / 10;
    const days = activeDays(current, hiddenId);

    const isFirstMonth = !oldDiamonds && !publisher.previousTier && !publisher.previousMonthDiamonds;
    const previousTier = isFirstMonth ? null : getTierNo(oldDiamonds);
    const currentTier = getTierNo(diamonds);
    const managerUsername = publisher.assignedEmployeeUsername || publisher.employeeUsername || publisher.managerUsername || publisher.representative || "";

    const eligibility = calculateRewardEligibility({
      activeDays: days,
      liveHours,
      monthlyDiamonds: diamonds,
      previousTier,
      isFirstMonth,
      matrix,
    });

    const username = publisher.username || publisher.userName || "-";
    const evalKey = hiddenId ? hiddenId.toLowerCase() : normalizeUsername(username);
    const evaluation = storedEvals[evalKey] || null;

    let finalStatus = eligibility.status;
    if (evaluation?.decision === "APPROVED") {
      finalStatus = "MANUAL_APPROVED";
    } else if (evaluation?.decision === "REJECTED") {
      finalStatus = "MANUAL_REJECTED";
    }

    const defaultGift = eligibility.defaultReward;
    const assignedGift = evaluation?.giftDetail || publisher.assignedGift || defaultGift;
    const deliveryStatus = evaluation?.deliveryStatus || (assignedGift.toLowerCase().includes("elmas") ? "Hazırlanıyor" : "Talep Bekleniyor");

    return {
      publisherId: publisher.id || publisher.userId || null,
      publisherUsername: username,
      publisherName: publisher.fullName || publisher.name || username || "-",
      hiddenId,
      managerUsername,
      managerName: publisher.managerName || managerMap[managerUsername] || managerUsername || "-",
      previousTier: previousTier || 1,
      isFirstMonth,
      currentTier,
      tierChange: isFirstMonth ? "Yeni Yayıncı (1. Ay)" : tierChange(previousTier || 1, currentTier),
      activeDays: days,
      liveCount,
      liveSeconds,
      liveHours,
      diamonds,
      eligibility,
      finalStatus,
      evaluation,
      assignedGift,
      deliveryStatus,
      deliveryNote: evaluation?.deliveryNote || "",
      trackingCode: evaluation?.trackingCode || "",
    };
  });
}

function useMonthlyActivity(publisherRows, managerRows, periodMonth, matrix = []) {
  const [state, setState] = useState({ loading: true, error: "", rows: [] });
  const [evalCounter, setEvalCounter] = useState(0);

  const refreshEvaluations = useCallback(() => {
    setEvalCounter((c) => c + 1);
  }, []);

  useEffect(() => {
    let ignore = false;
    async function load() {
      const uniquePublishers = deduplicatePublishersByHiddenId(publisherRows || []);
      
      const stored = loadStoredEvaluations();
      const monthPrefix = `${periodMonth}:`;
      const currentMonthEvals = {};
      Object.keys(stored).forEach((k) => {
        if (k.startsWith(monthPrefix)) {
          const subKey = k.slice(monthPrefix.length);
          currentMonthEvals[subKey] = stored[k];
        }
      });

      // 🌐 1. PHP Sunucusundan bu aya ait yayınlanmış veya taslak ödülleri çek ve birleştir
      let serverAwards = [];
      try {
        const serverRes = await requestJson(`${API_GIFT_AWARD_LIST}?periodMonth=${encodeURIComponent(periodMonth)}`, { cache: "no-store" });
        if (serverRes?.success && Array.isArray(serverRes.data)) {
          serverAwards = serverRes.data;
          serverAwards.forEach((item) => {
            const hKey = item.hidden_id ? item.hidden_id.toLowerCase().trim() : null;
            const uKey = item.publisher_username ? normalizeUsername(item.publisher_username) : null;
            const evalObj = {
              decision: item.publish_status === "Yayınlandı" ? "APPROVED" : (item.publish_status === "Yayından Kaldırıldı" ? "UNPUBLISHED" : "PENDING"),
              giftDetail: item.gift_detail,
              publishStatus: item.publish_status,
              deliveryStatus: item.delivery_status || "Talep Bekleniyor",
              evaluationNote: item.admin_note || "",
              trackingCode: item.tracking_code || "",
              deliveryNote: item.delivery_note || "",
              evaluatedBy: item.created_by || "admin",
              evaluatedAt: item.updated_at || item.created_at,
              currentTier: item.current_tier,
              rawAward: item,
            };
            if (hKey) currentMonthEvals[hKey] = { ...(currentMonthEvals[hKey] || {}), ...evalObj };
            if (uKey) currentMonthEvals[uKey] = { ...(currentMonthEvals[uKey] || {}), ...evalObj };
          });
        }
      } catch (err) {
        console.warn("Sunucu ödülleri yüklenemedi:", err.message);
      }

      // 🌐 2. Eğer sunucuda manuel / Excel ile eklenmiş ancak publisherRows içinde bulunmayan yayıncılar varsa listeye dahil et
      const existingUsernames = new Set(uniquePublishers.map((p) => normalizeUsername(p.username || p.userName)));
      const existingHiddenIds = new Set(uniquePublishers.map((p) => getHiddenId(p).toLowerCase()).filter((id) => id && id !== "-"));
      const synthesizedPublishers = [...uniquePublishers];

      serverAwards.forEach((item) => {
        const hId = item.hidden_id ? item.hidden_id.trim() : "";
        const uName = item.publisher_username ? item.publisher_username.trim() : "";
        const normU = normalizeUsername(uName);
        if ((!normU || !existingUsernames.has(normU)) && (!hId || !existingHiddenIds.has(hId.toLowerCase()))) {
          if (normU) existingUsernames.add(normU);
          if (hId) existingHiddenIds.add(hId.toLowerCase());
          synthesizedPublishers.push({
            id: item.id || `award_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            username: uName,
            name: item.publisher_name || uName,
            fullName: item.publisher_name || uName,
            hiddenId: hId,
            managerName: item.manager_name || "-",
            currentTier: item.current_tier || 1,
            assignedGift: item.gift_detail || "",
          });
        }
      });

      const finalHiddenIds = synthesizedPublishers.map(getHiddenId).filter((id) => id && id !== "-");

      if (!finalHiddenIds.length) {
        setState({
          loading: false,
          error: "",
          rows: buildActivityRows(synthesizedPublishers, managerRows, { publishers: [], publisherDaily: [] }, { publishers: [], publisherDaily: [] }, currentMonthEvals, matrix),
        });
        return;
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: "" }));
        const [current, previous] = await Promise.all([
          fetchPerformance(finalHiddenIds, monthRange(periodMonth, 0)),
          fetchPerformance(finalHiddenIds, monthRange(periodMonth, -1)),
        ]);
        if (!ignore) {
          setState({
            loading: false,
            error: "",
            rows: buildActivityRows(synthesizedPublishers, managerRows, current, previous, currentMonthEvals, matrix),
          });
        }
      } catch (error) {
        if (!ignore) {
          setState({
            loading: false,
            error: error.message || "Performans verisi alınamadı.",
            rows: buildActivityRows(synthesizedPublishers, managerRows, { publishers: [], publisherDaily: [] }, { publishers: [], publisherDaily: [] }, currentMonthEvals, matrix),
          });
        }
      }
    }
    load();
    return () => { ignore = true; };
  }, [publisherRows, managerRows, periodMonth, evalCounter, matrix]);

  return { ...state, refreshEvaluations };
}

function Card({ children, className = "" }) {
  return <div className={`rounded-[1.45rem] border border-white/[0.08] bg-white/[0.03] shadow-[0_18px_60px_rgba(0,0,0,.32)] ${className}`}>{children}</div>;
}

function Button({ children, onClick, variant = "dark", disabled = false, type = "button", className = "" }) {
  const tone = variant === "gold"
    ? "border-yellow-500/35 bg-yellow-500 text-black font-black hover:bg-yellow-400"
    : variant === "red"
      ? "border-red-500/25 bg-red-500/10 text-red-200 hover:bg-red-500/20"
      : variant === "green"
        ? "border-emerald-500/25 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500 hover:text-black font-black"
        : "border-white/10 bg-white/[0.04] text-white/70 hover:border-yellow-500/30 hover:text-yellow-200";
  return <button type={type} onClick={onClick} disabled={disabled} className={`rounded-xl border px-4 py-2.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-45 ${tone} ${className}`}>{children}</button>;
}

function SectionHeader({ title, desc, action }) {
  return (
    <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.34em] text-yellow-300/80">Wolf Media • Hediye ve Prim Sistemi</div>
        <h2 className="mt-2 text-2xl font-black text-white lg:text-3xl">{title}</h2>
        <p className="mt-2 max-w-4xl text-[13px] leading-6 text-white/50">{desc}</p>
      </div>
      {action}
    </div>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-[11px] font-bold text-white/45">{label}</span>
      <select value={value} onChange={onChange} className="w-full rounded-xl border border-white/10 bg-[#0b0b0b] px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-500/35 [color-scheme:dark]">{children}</select>
    </label>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-[11px] font-bold text-white/45">{label}</span>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full rounded-xl border border-white/10 bg-[#0b0b0b] px-3 py-2.5 text-xs text-white outline-none placeholder:text-white/25 focus:border-yellow-500/35" />
    </label>
  );
}

function MiniMetric({ label, value }) {
  return <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3"><div className="text-[9px] font-black uppercase tracking-[0.16em] text-white/35">{label}</div><div className="mt-1 break-words text-sm font-black text-white">{value}</div></div>;
}

/* ==========================================================================
   MANUEL DEĞERLENDİRME VE ONAY/RED MODALI
   ========================================================================== */
function ManualReviewModal({ row, periodMonth, currentAdmin, onClose, onEvaluated }) {
  const [giftDetail, setGiftDetail] = useState(row?.assignedGift || row?.eligibility?.defaultReward || "");
  const [evaluationNote, setEvaluationNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!row) return null;

  async function handleDecision(decision) {
    if (decision === "REJECTED" && !evaluationNote.trim()) {
      setError("Reddetme işleminde açıklama gerekçesi yazılması zorunludur.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
      const payload = {
        decision,
        evaluatedBy: currentAdmin?.username || currentAdmin?.fullName || "admin",
        evaluatedAt: new Date().toISOString(),
        evaluationNote: evaluationNote.trim(),
        giftDetail: decision === "APPROVED" ? (giftDetail.trim() || row?.eligibility?.defaultReward) : "-",
        deliveryStatus: decision === "APPROVED" ? (giftDetail.toLowerCase().includes("elmas") ? "Hazırlanıyor" : "Talep Bekleniyor") : "İptal",
      };

      saveStoredEvaluation(periodMonth, identifier, payload);

      try {
        await requestJson(API_GIFT_AWARD_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            ...row,
            giftDetail: payload.giftDetail,
            adminNote: `[${decision === "APPROVED" ? "İSTİSNA ONAYLANDI" : "REDDEDİLDİ"}] ${evaluationNote.trim()}`,
            publishStatus: decision === "APPROVED" ? "Yayınlandı" : "Taslak",
            deliveryStatus: payload.deliveryStatus,
            createdBy: currentAdmin?.username || "admin",
          }),
        });
      } catch {
        // Devam
      }

      onEvaluated?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "İşlem kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-[1.8rem] border border-yellow-500/30 bg-[#090909] p-5 sm:p-7 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <span className="inline-flex rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">
              Manuel Değerlendirme Havuzu (2/3 Şart)
            </span>
            <h3 className="mt-2 text-2xl font-black text-white">{row.publisherUsername}</h3>
            <div className="mt-1 text-xs text-white/40">{row.publisherName} • {periodLabel(periodMonth)}</div>
          </div>
          <Button onClick={onClose}>Kapat</Button>
        </div>

        <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/[0.07] p-4">
          <div className="text-xs font-black text-amber-300">İNCELENEN EKSİK ŞART:</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {row.eligibility.missingConditions.map((cond, idx) => (
              <span key={idx} className="rounded-xl border border-amber-400/40 bg-amber-400/15 px-3 py-1.5 text-xs font-black text-amber-200">
                ⚠️ {cond.label} ({cond.detail})
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <MiniMetric label="Mevcut Kademe" value={`Kademe ${row.currentTier}`} />
          <MiniMetric label="Aktif Gün (Hedef: 22)" value={`${row.activeDays} Gün`} />
          <MiniMetric label="Yayın Saati (Hedef: 80)" value={`${row.liveHours} Saat`} />
          <MiniMetric label="Aylık Elmas (Hedef: 40K)" value={formatNumber(row.diamonds)} />
          <MiniMetric label="Yönetici" value={row.managerName || "-"} />
          <MiniMetric label="Hidden ID" value={row.hiddenId || "-"} />
        </div>

        <label className="mt-5 block">
          <span className="mb-2 block text-xs font-black text-white/70">Onaylanacak Hediye / Prim Detayı</span>
          <input
            value={giftDetail}
            onChange={(e) => setGiftDetail(e.target.value)}
            placeholder="Örn: 25.000 Elmas, Kulaklık & Mikrofon..."
            className="w-full rounded-2xl border border-yellow-500/30 bg-black/50 px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-yellow-400"
          />
        </label>

        <label className="mt-4 block">
          <span className="mb-2 block text-xs font-black text-white/70">Yönetici Değerlendirme Notu / İstisna Gerekçesi</span>
          <textarea
            value={evaluationNote}
            onChange={(e) => setEvaluationNote(e.target.value)}
            rows={3}
            placeholder="İstisna onay sebebi veya red gerekçesini yazınız..."
            className="w-full rounded-2xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-white outline-none focus:border-yellow-500/35"
          />
        </label>

        {error && <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-200">{error}</div>}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          <Button variant="red" onClick={() => handleDecision("REJECTED")} disabled={saving}>
            ✕ Reddet
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose}>İptal</Button>
            <Button variant="gold" onClick={() => handleDecision("APPROVED")} disabled={saving}>
              ✓ İstisnayı Onayla ve Ödülü Tanımla
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   TESLİMAT VE KARGO DÜZENLEME MODALI
   ========================================================================== */
function DeliveryEditModal({ row, periodMonth, currentAdmin, onClose, onSaved }) {
  const [deliveryStatus, setDeliveryStatus] = useState(row?.deliveryStatus || "Talep Bekleniyor");
  const [trackingCode, setTrackingCode] = useState(row?.trackingCode || "");
  const [deliveryNote, setDeliveryNote] = useState(row?.deliveryNote || "");
  const [saving, setSaving] = useState(false);

  if (!row) return null;

  async function handleSave() {
    try {
      setSaving(true);
      const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
      saveStoredEvaluation(periodMonth, identifier, {
        ...(row.evaluation || {}),
        deliveryStatus,
        trackingCode: trackingCode.trim(),
        deliveryNote: deliveryNote.trim(),
        deliveryUpdatedBy: currentAdmin?.username || "admin",
        deliveryUpdatedAt: new Date().toISOString(),
      });

      if (row.id) {
        try {
          await requestJson(API_GIFT_AWARD_DELIVERY_UPDATE, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: row.id,
              deliveryStatus,
              trackingCode: trackingCode.trim(),
              adminNote: deliveryNote.trim(),
              updatedBy: currentAdmin?.username || "admin",
            }),
          });
        } catch {
          // Devam
        }
      }

      onSaved?.();
      onClose?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-[1.8rem] border border-yellow-500/30 bg-[#090909] p-5 sm:p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <h3 className="text-xl font-black text-white">Teslimat Bilgilerini Güncelle</h3>
            <div className="mt-1 text-xs text-white/40">{row.publisherUsername} • {row.assignedGift}</div>
          </div>
          <Button onClick={onClose}>Kapat</Button>
        </div>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="mb-2 block text-xs font-black text-white/70">Teslimat Durumu</span>
            <select
              value={deliveryStatus}
              onChange={(e) => setDeliveryStatus(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
            >
              <option value="Talep Bekleniyor">Talep Bekleniyor (Fiziksel Ürün - Varsayılan)</option>
              <option value="Bekliyor">Bekliyor</option>
              <option value="Hazırlanıyor">Hazırlanıyor</option>
              <option value="Kargoya Verildi">Kargoya Verildi</option>
              <option value="Teslim Edildi">Teslim Edildi</option>
              <option value="İptal">İptal</option>
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-black text-white/70">Kargo Takip No / Gönderim Kodu</span>
            <input
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              placeholder="Örn: YK-8492049102"
              className="w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm text-white outline-none focus:border-yellow-400"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-black text-white/70">Teslimat Açıklaması / Adres Notu</span>
            <textarea
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              rows={3}
              placeholder="Teslimat detayları..."
              className="w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm text-white outline-none focus:border-yellow-400"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-white/10 pt-4">
          <Button onClick={onClose}>İptal</Button>
          <Button variant="gold" onClick={handleSave} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   ANA BİLEŞEN: GIFT ACTIVE DETAILS & 6 SEKMELİ YÖNETİM
   ========================================================================== */
export function GiftActiveDetailsContent({ publisherRows = [], managerRows = [], currentAdmin = {}, initialTab = "tiktok_calculator" }) {
  const latestPeriod = useMemo(() => getLatestUploadedPeriod(), []);
  const [periodMonth, setPeriodMonth] = useState(() => getLatestUploadedPeriod());
  const [giftMatrix, setGiftMatrix] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY_MATRIX_CACHE);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [matrixMessage, setMatrixMessage] = useState("");

  // PHP Sunucusundan Hediye Matrisini Yükle
  const loadMatrixFromPhp = useCallback(async () => {
    try {
      const res = await requestJson(API_GIFT_MATRIX_GET, { cache: "no-store" });
      if (res?.success && Array.isArray(res.matrix)) {
        setGiftMatrix(res.matrix);
        try {
          localStorage.setItem(STORAGE_KEY_MATRIX_CACHE, JSON.stringify(res.matrix));
        } catch {}
      }
    } catch (err) {
      console.warn("Hediye matrisi PHP'den yüklenemedi:", err.message);
    }
  }, []);

  useEffect(() => {
    loadMatrixFromPhp();
  }, [loadMatrixFromPhp]);

  const { loading, error, rows, refreshEvaluations } = useMonthlyActivity(publisherRows, managerRows, periodMonth, giftMatrix);
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [manualModalInitialData, setManualModalInitialData] = useState(null);

  const [filters, setFilters] = useState({
    manager: "",
    publisher: "",
    tier: "",
    deliveryStatus: "",
  });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeFiltersCount = (filters.publisher ? 1 : 0) + (filters.manager ? 1 : 0) + (filters.tier ? 1 : 0) + (filters.deliveryStatus ? 1 : 0);
  const handleClearFilters = () => {
    setFilters({ manager: "", publisher: "", tier: "", deliveryStatus: "" });
    setFilterPanelOpen(false);
  };

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [reviewingRow, setReviewingRow] = useState(null);
  const [deliveryEditingRow, setDeliveryEditingRow] = useState(null);
  const [customAwardRow, setCustomAwardRow] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const handleOpenManualGiftForPublisher = (initial = {}) => {
    setManualModalInitialData(initial);
    setIsManualModalOpen(true);
  };

  const managers = useMemo(() => [...new Set(rows.map((r) => r.managerName).filter((v) => v && v !== "-"))].sort(), [rows]);

  const counts = useMemo(() => {
    const auto = rows.filter((r) => r.eligibility.status === "AUTO_ELIGIBLE").length;
    const manual = rows.filter((r) => r.eligibility.status === "MANUAL_REVIEW" && !r.evaluation?.decision).length;
    const approved = rows.filter((r) => r.evaluation?.decision === "APPROVED").length;
    const rejected = rows.filter((r) => r.evaluation?.decision === "REJECTED").length;
    const delivery = rows.filter((r) => r.eligibility.status === "AUTO_ELIGIBLE" || r.evaluation?.decision === "APPROVED").length;
    return { auto, manual, approved, rejected, delivery, all: rows.length };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (activeTab === "auto_eligible" && row.eligibility.status !== "AUTO_ELIGIBLE") return false;
      if (activeTab === "manual_review" && (row.eligibility.status !== "MANUAL_REVIEW" || Boolean(row.evaluation?.decision))) return false;
      if (activeTab === "approved_exceptions" && row.evaluation?.decision !== "APPROVED") return false;
      if (activeTab === "rejected" && row.evaluation?.decision !== "REJECTED") return false;
      if (activeTab === "delivery_tracking" && !(row.eligibility.status === "AUTO_ELIGIBLE" || row.evaluation?.decision === "APPROVED")) return false;

      const q = normalizeUsername(filters.publisher);
      if (q && !normalizeUsername(`${row.publisherUsername} ${row.publisherName} ${row.hiddenId}`).includes(q)) return false;
      if (filters.manager && row.managerName !== filters.manager) return false;
      if (filters.tier && String(row.currentTier) !== filters.tier) return false;
      if (filters.deliveryStatus && row.deliveryStatus !== filters.deliveryStatus) return false;

      return true;
    });
  }, [rows, activeTab, filters]);

  // Checkbox Seçim Yönetimi
  const getRowKey = (row) => `${row.hiddenId || ""}_${row.publisherUsername || ""}`;

  const isAllFilteredSelected = useMemo(() => {
    if (!filteredRows.length) return false;
    return filteredRows.every((r) => selectedKeys.has(getRowKey(r)));
  }, [filteredRows, selectedKeys]);

  const handleToggleSelectAll = () => {
    if (isAllFilteredSelected) {
      setSelectedKeys(new Set());
    } else {
      const next = new Set(selectedKeys);
      filteredRows.forEach((r) => next.add(getRowKey(r)));
      setSelectedKeys(next);
    }
  };

  const handleToggleRow = (row) => {
    const key = getRowKey(row);
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedKeys(next);
  };

  // 🟢 1. Tekil Yayınlama (Kişi Bazlı)
  async function publishSingle(row, customGiftText = null) {
    try {
      setActionMessage(`${row.publisherUsername} için ödül yayınlanıyor...`);
      const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
      const giftToSave = customGiftText || row.assignedGift || "Jet (5K)";
      
      saveStoredEvaluation(periodMonth, identifier, {
        decision: "APPROVED",
        evaluatedBy: currentAdmin?.username || "admin",
        giftDetail: giftToSave,
        deliveryStatus: row.deliveryStatus || "Talep Bekleniyor",
        publishStatus: "Yayınlandı",
      });

      try {
        await requestJson(API_GIFT_AWARD_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            ...row,
            giftDetail: giftToSave,
            publishStatus: "Yayınlandı",
            deliveryStatus: row.deliveryStatus || "Talep Bekleniyor",
            createdBy: currentAdmin?.username || "admin",
          }),
        });
      } catch {}

      setActionMessage(`✓ ${row.publisherUsername} ödülü (${giftToSave}) başarıyla yayınlandı.`);
      refreshEvaluations();
    } catch {
      setActionMessage(`✓ ${row.publisherUsername} ödülü yerel olarak yayınlandı.`);
      refreshEvaluations();
    }
  }

  // 🔴 2. Tekil Yayından Kaldırma (Kişi Bazlı)
  async function unpublishSingle(row) {
    try {
      setActionMessage(`${row.publisherUsername} için ödül yayından kaldırılıyor...`);
      const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
      saveStoredEvaluation(periodMonth, identifier, {
        decision: "UNPUBLISHED",
        evaluatedBy: currentAdmin?.username || "admin",
        giftDetail: row.assignedGift,
        deliveryStatus: "İptal",
        publishStatus: "Yayından Kaldırıldı",
      });

      try {
        await requestJson(API_GIFT_AWARD_UNPUBLISH, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            hiddenId: row.hiddenId,
            publisherUsername: row.publisherUsername,
            publishStatus: "Yayından Kaldırıldı",
            canceledBy: currentAdmin?.username || "admin",
          }),
        });
      } catch {}

      setActionMessage(`✓ ${row.publisherUsername} ödülü yayından kaldırıldı.`);
      refreshEvaluations();
    } catch {
      setActionMessage(`✓ ${row.publisherUsername} ödülü yerel olarak yayından kaldırıldı.`);
      refreshEvaluations();
    }
  }

  // 📦 3. Seçilenleri Toplu Yayınla (Batch Publish Selected)
  async function handleBulkPublishSelected() {
    const selectedRows = filteredRows.filter((r) => selectedKeys.has(getRowKey(r)));
    if (!selectedRows.length) return;

    try {
      setIsBulkProcessing(true);
      setActionMessage(`${selectedRows.length} seçili yayıncının ödülü yayınlanıyor...`);

      // Lokal kaydet
      selectedRows.forEach((row) => {
        const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
        saveStoredEvaluation(periodMonth, identifier, {
          decision: "APPROVED",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: row.assignedGift,
          deliveryStatus: row.deliveryStatus || "Talep Bekleniyor",
          publishStatus: "Yayınlandı",
        });
      });

      // PHP API Toplu Kayıt
      try {
        await requestJson(API_GIFT_AWARD_BULK_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            items: selectedRows.map((r) => ({
              ...r,
              giftDetail: r.assignedGift,
              publishStatus: "Yayınlandı",
              deliveryStatus: "Talep Bekleniyor"
            })),
            publishStatus: "Yayınlandı",
            createdBy: currentAdmin?.username || "admin"
          })
        });
      } catch {}

      setActionMessage(`✓ ${selectedRows.length} adet seçili yayıncının ödülü başarıyla yayınlandı.`);
      setSelectedKeys(new Set());
      refreshEvaluations();
    } catch (err) {
      setActionMessage(`✕ Toplu yayınlama hatası: ${err.message}`);
    } finally {
      setIsBulkProcessing(false);
    }
  }

  // 🗑️ 4. Seçilenleri Toplu Yayından Kaldır (Batch Unpublish Selected)
  async function handleBulkUnpublishSelected() {
    const selectedRows = filteredRows.filter((r) => selectedKeys.has(getRowKey(r)));
    if (!selectedRows.length) return;

    try {
      setIsBulkProcessing(true);
      setActionMessage(`${selectedRows.length} seçili yayıncının ödülü yayından kaldırılıyor...`);

      // Lokal kaydet
      selectedRows.forEach((row) => {
        const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
        saveStoredEvaluation(periodMonth, identifier, {
          decision: "UNPUBLISHED",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: row.assignedGift,
          deliveryStatus: "İptal",
          publishStatus: "Yayından Kaldırıldı",
        });
      });

      // PHP API Toplu Kaldır
      try {
        await requestJson(API_GIFT_AWARD_BULK_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            items: selectedRows.map((r) => ({
              ...r,
              publishStatus: "Yayından Kaldırıldı",
              deliveryStatus: "İptal"
            })),
            publishStatus: "Yayından Kaldırıldı",
            createdBy: currentAdmin?.username || "admin"
          })
        });
      } catch {}

      setActionMessage(`✓ ${selectedRows.length} adet seçili yayıncının ödülü yayından kaldırıldı.`);
      setSelectedKeys(new Set());
      refreshEvaluations();
    } catch (err) {
      setActionMessage(`✕ Toplu yayından kaldırma hatası: ${err.message}`);
    } finally {
      setIsBulkProcessing(false);
    }
  }

  // 🌟 5. Tümünü Yayınla (Tümünü Ekle)
  async function handlePublishAllFiltered() {
    if (!filteredRows.length) return;
    const confirm = window.confirm(`Bu listedeki toplam ${filteredRows.length} yayıncının ödülünü tek tıkla yayınlamak istiyor musunuz?`);
    if (!confirm) return;

    try {
      setIsBulkProcessing(true);
      setActionMessage(`Tüm listedeki ${filteredRows.length} yayıncının ödülü yayınlanıyor...`);

      filteredRows.forEach((row) => {
        const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
        saveStoredEvaluation(periodMonth, identifier, {
          decision: "APPROVED",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: row.assignedGift,
          deliveryStatus: row.deliveryStatus || "Talep Bekleniyor",
          publishStatus: "Yayınlandı",
        });
      });

      try {
        await requestJson(API_GIFT_AWARD_BULK_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            items: filteredRows.map((r) => ({
              ...r,
              giftDetail: r.assignedGift,
              publishStatus: "Yayınlandı",
              deliveryStatus: "Talep Bekleniyor"
            })),
            publishStatus: "Yayınlandı",
            createdBy: currentAdmin?.username || "admin"
          })
        });
      } catch {}

      setActionMessage(`✓ Toplam ${filteredRows.length} yayıncının ödülü başarıyla yayınlandı.`);
      setSelectedKeys(new Set());
      refreshEvaluations();
    } catch (err) {
      setActionMessage(`✕ Tümünü yayınlama hatası: ${err.message}`);
    } finally {
      setIsBulkProcessing(false);
    }
  }

  // 🗑️ 6. Tümünü Yayından Kaldır (Tümünü Kaldır)
  async function handleUnpublishAllFiltered() {
    if (!filteredRows.length) return;
    const confirm = window.confirm(`${periodLabel(periodMonth)} dönemine ait tüm yayıncıların ödüllerini yayından kaldırmak istiyor musunuz?`);
    if (!confirm) return;

    try {
      setIsBulkProcessing(true);
      setActionMessage("Tüm ödüller yayından kaldırılıyor...");

      filteredRows.forEach((row) => {
        const identifier = row.hiddenId || normalizeUsername(row.publisherUsername);
        saveStoredEvaluation(periodMonth, identifier, {
          decision: "UNPUBLISHED",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: row.assignedGift,
          deliveryStatus: "İptal",
          publishStatus: "Yayından Kaldırıldı",
        });
      });

      try {
        await requestJson(API_GIFT_AWARD_BULK_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            periodMonth,
            unpublishAll: true,
            createdBy: currentAdmin?.username || "admin"
          })
        });
      } catch {}

      setActionMessage(`✓ ${periodLabel(periodMonth)} dönemine ait tüm ödüller başarıyla yayından kaldırıldı.`);
      setSelectedKeys(new Set());
      refreshEvaluations();
    } catch (err) {
      setActionMessage(`✕ Tümünü yayından kaldırma hatası: ${err.message}`);
    } finally {
      setIsBulkProcessing(false);
    }
  }

  // Matris Düzenleme Fonksiyonları
  function handleMatrixCellChange(tierNo, field, value) {
    setGiftMatrix((prev) =>
      prev.map((item) =>
        Number(item.tierNo) === Number(tierNo)
          ? { ...item, [field]: value }
          : item
      )
    );
  }

  async function handleSaveMatrixToPhp() {
    try {
      setMatrixSaving(true);
      setMatrixMessage("");
      const res = await requestJson(API_GIFT_MATRIX_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ matrix: giftMatrix }),
      });
      try {
        localStorage.setItem(STORAGE_KEY_MATRIX_CACHE, JSON.stringify(giftMatrix));
      } catch {}
      setMatrixMessage(res?.message || "✓ Hediye matrisi PHP sunucusuna başarıyla kaydedildi.");
      refreshEvaluations();
    } catch (err) {
      setMatrixMessage(`✕ Hata: ${err.message}`);
    } finally {
      setMatrixSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* 🌟 GENEL ÜST YÖNETİM ÇUBUĞU (Kompakt Tarih Seçimi & Eylemler) */}
      <div className="rounded-3xl border border-yellow-500/20 bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-transparent p-4 sm:p-5 backdrop-blur-md">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-yellow-300">
              <span>WOLF MEDIA • HEDİYE VE PRİM SİSTEMİ</span>
            </div>
            <h2 className="mt-1.5 text-xl sm:text-2xl font-black text-white">Kademe ve Hediye / Prim Yönetimi</h2>
            <p className="mt-0.5 text-xs text-white/60">
              Aylık takvim dönemi bazında K1–K10 kademeleri ve TikTok Aktiflik & Yükselme hakedişleri.
            </p>
          </div>

          {/* Tarih / Dönem Seçici & Eylemler */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Kompakt Genel Dönem Seçici */}
            <div className="flex items-center gap-2 rounded-2xl border border-yellow-500/30 bg-black/70 px-3.5 py-2 shadow-sm">
              <span className="text-xs font-black text-yellow-300 whitespace-nowrap">📅 Dönem:</span>
              <input
                type="month"
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
                className="bg-transparent text-xs font-black text-white outline-none cursor-pointer [color-scheme:dark]"
              />
              {latestPeriod && periodMonth !== latestPeriod && (
                <button
                  type="button"
                  onClick={() => setPeriodMonth(latestPeriod)}
                  className="rounded-lg bg-yellow-500/20 px-2 py-0.5 text-[10px] font-black text-yellow-300 hover:bg-yellow-500 hover:text-black transition whitespace-nowrap"
                  title="En son veri yüklenen döneme git"
                >
                  ⚡ Son Dönem ({periodLabel(latestPeriod)})
                </button>
              )}
            </div>

            {/* Ortak Eylem Butonları */}
            <Button
              variant="gold"
              onClick={() => setIsManualModalOpen(true)}
              title="Yayıncıya manuel hediye/prim tanımlayın"
            >
              ➕ Manuel Hediye Ekle
            </Button>
            <Button
              className="border-cyan-500/30 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 font-bold"
              onClick={() => setIsBulkModalOpen(true)}
              title="Excel (.xlsx, .xls) veya CSV dosyasından toplu hediye yükleyin"
            >
              📥 Excel'den Toplu Yükle
            </Button>
            <Button
              onClick={refreshEvaluations}
              title="Sunucudaki ve tablodaki tüm aktiflik detaylarını yeniden senkronize et"
            >
              🔄 Yenile
            </Button>

            {/* Standart Sekmelerde Toplu Yayınlama / İndirme */}
            {activeTab !== "tiktok_calculator" && activeTab !== "gift_matrix" && (
              <>
                <Button
                  variant="green"
                  onClick={handlePublishAllFiltered}
                  disabled={isBulkProcessing || !filteredRows.length}
                  title="Bu listedeki tüm yayıncıların ödüllerini tek tıkla yayınla"
                >
                  🌟 Tümünü Yayınla ({filteredRows.length})
                </Button>
                <Button
                  variant="red"
                  onClick={handleUnpublishAllFiltered}
                  disabled={isBulkProcessing || !filteredRows.length}
                  title="Bu döneme ait tüm ödülleri yayından kaldır"
                >
                  🗑️ Tümünü Kaldır
                </Button>
                <Button
                  onClick={() => downloadExcel(`hediye-prim-${periodMonth}.xls`, filteredRows)}
                  disabled={!filteredRows.length}
                >
                  Excel İndir ({filteredRows.length})
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Top Section Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3">
        <button
          type="button"
          onClick={() => setActiveTab("tiktok_calculator")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "tiktok_calculator"
              ? "border border-amber-400 bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-yellow-200 shadow-xl"
              : "border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20"
          }`}
        >
          <span>🔥 Aylık Hediye Hesaplama (Aktiflik & Yükselme)</span>
          <span className="rounded-full bg-yellow-500/40 px-2 py-0.5 text-[10px] text-yellow-100 font-black">YENİ</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("auto_eligible")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "auto_eligible"
              ? "border border-emerald-500/40 bg-emerald-500/20 text-emerald-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>🎯 1. Otomatik Hak Kazananlar</span>
          <span className="rounded-full bg-emerald-500/30 px-2 py-0.5 text-[10px] text-emerald-100">{counts.auto}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("manual_review")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "manual_review"
              ? "border border-amber-500/40 bg-amber-500/20 text-amber-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>⚖️ 2. Değerlendirme Bekleyenler (2/3 Havuz)</span>
          <span className="rounded-full bg-amber-500/30 px-2 py-0.5 text-[10px] text-amber-100">{counts.manual}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("approved_exceptions")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "approved_exceptions"
              ? "border border-cyan-500/40 bg-cyan-500/20 text-cyan-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>✓ 3. Onaylanan İstisnalar</span>
          <span className="rounded-full bg-cyan-500/30 px-2 py-0.5 text-[10px] text-cyan-100">{counts.approved}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("rejected")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "rejected"
              ? "border border-red-500/40 bg-red-500/20 text-red-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>✕ 4. Reddedilenler</span>
          <span className="rounded-full bg-red-500/30 px-2 py-0.5 text-[10px] text-red-100">{counts.rejected}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("delivery_tracking")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "delivery_tracking"
              ? "border border-yellow-500/40 bg-yellow-500/20 text-yellow-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>📦 5. Hediye / Teslimat Takibi</span>
          <span className="rounded-full bg-yellow-500/30 px-2 py-0.5 text-[10px] text-yellow-100">{counts.delivery}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("all_activity")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "all_activity"
              ? "border border-purple-500/40 bg-purple-500/20 text-purple-200 shadow-lg"
              : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
          }`}
        >
          <span>📊 Tüm Yayıncı Aktiflikleri</span>
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">{counts.all}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("gift_matrix")}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${
            activeTab === "gift_matrix"
              ? "border border-yellow-400 bg-yellow-500/25 text-yellow-200 shadow-lg"
              : "border border-yellow-500/20 bg-yellow-500/[0.05] text-yellow-300/80 hover:text-yellow-200 hover:bg-yellow-500/10"
          }`}
        >
          <span>🎁 Hediye Tanımları (PHP Matrisi)</span>
        </button>
      </div>

      {actionMessage && (
        <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-xs font-bold text-yellow-200 flex items-center justify-between">
          <span>{actionMessage}</span>
          <button type="button" onClick={() => setActionMessage("")} className="text-yellow-400 hover:text-white text-xs">✕</button>
        </div>
      )}

      {/* TIKTOK CALCULATOR TAB */}
      {activeTab === "tiktok_calculator" ? (
        <TikTokActivityProgressionContent
          publisherRows={publisherRows}
          managerRows={managerRows}
          currentAdmin={currentAdmin}
          periodMonth={periodMonth}
          setPeriodMonth={setPeriodMonth}
          onOpenManualModal={handleOpenManualGiftForPublisher}
          onRefreshEvaluations={refreshEvaluations}
        />
      ) : activeTab === "gift_matrix" ? (
        <div className="space-y-4">
          <Card className="p-5 sm:p-6 border-yellow-500/20 bg-black/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-white/10 pb-4">
              <div>
                <span className="inline-flex rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300">
                  PHP Sunucu Tabanlı Hediye Matrisi
                </span>
                <h3 className="mt-2 text-xl font-black text-white">Kademe ve Hediye / Prim Tanımları</h3>
                <p className="mt-1 text-xs text-white/50">
                  Tüm hediye isimleri ve kademe eşleşmeleri frontend kodlarında görünmez; doğrudan PHP backend API üzerinden çalışır. Değişiklik yaptığınızda sunucuya kaydedebilir veya PHP dosyasını doğrudan sunucudan düzenleyebilirsiniz.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={loadMatrixFromPhp} disabled={matrixSaving}>
                  🔄 Sunucudan Yenile
                </Button>
                <Button variant="gold" onClick={handleSaveMatrixToPhp} disabled={matrixSaving}>
                  {matrixSaving ? "Kaydediliyor..." : "💾 PHP Sunucusuna Kaydet"}
                </Button>
              </div>
            </div>

            {matrixMessage && (
              <div className={`mt-4 rounded-xl border px-4 py-3 text-xs font-bold ${
                matrixMessage.includes("✕") || matrixMessage.includes("Hata")
                  ? "border-red-500/30 bg-red-500/10 text-red-200"
                  : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              }`}>
                {matrixMessage}
              </div>
            )}

            <div className="mt-5 overflow-x-auto">
              <table className="min-w-[950px] w-full border-collapse text-left text-xs">
                <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
                  <tr>
                    <th className="px-4 py-3.5 w-[140px]">Grup</th>
                    <th className="px-4 py-3.5 w-[180px]">Kademeler (Elmas Aralığı)</th>
                    <th className="px-4 py-3.5">Kademe Koruma</th>
                    <th className="px-4 py-3.5">Kademe Yükselme (+1)</th>
                    <th className="px-4 py-3.5">Kademe Yükselme (+PLUS)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {(giftMatrix || []).map((row) => (
                    <tr key={row.tierNo} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <span className="font-bold text-yellow-300/90">{row.group || "-"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-black text-white">{row.tierName}</div>
                        <div className="text-[10px] text-white/40">
                          {formatNumber(row.minDiamonds)} - {formatNumber(row.maxDiamonds)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.protectReward ?? ""}
                          onChange={(e) => handleMatrixCellChange(row.tierNo, "protectReward", e.target.value)}
                          placeholder="-"
                          className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-xs text-white outline-none focus:border-yellow-400"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.upgradeSingleReward ?? ""}
                          onChange={(e) => handleMatrixCellChange(row.tierNo, "upgradeSingleReward", e.target.value)}
                          placeholder="-"
                          className="w-full rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] px-3 py-2 text-xs font-bold text-emerald-200 outline-none focus:border-emerald-400"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.upgradePlusReward ?? ""}
                          onChange={(e) => handleMatrixCellChange(row.tierNo, "upgradePlusReward", e.target.value)}
                          placeholder="-"
                          className="w-full rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] px-3 py-2 text-xs font-bold text-cyan-200 outline-none focus:border-cyan-400"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      ) : (
        <>
          {/* SADELEŞTİRİLMİŞ FİLTRE VE ARAMA PANELİ */}
          <div className="rounded-2xl border border-white/10 bg-[#0d0d10] p-3.5 shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1">
                {/* Arama Input'u */}
                <div className="relative flex-1 max-w-md">
                  <span className="absolute left-3 top-2.5 text-xs text-white/30">🔍</span>
                  <input
                    type="text"
                    placeholder="Yayıncı kullanıcı adı, isim veya Hidden ID ara..."
                    value={filters.publisher}
                    onChange={(e) => setFilters((p) => ({ ...p, publisher: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black/60 pl-8 pr-8 py-2 text-xs text-white placeholder-white/30 outline-none focus:border-yellow-500/40"
                  />
                  {filters.publisher && (
                    <button
                      type="button"
                      onClick={() => setFilters((p) => ({ ...p, publisher: "" }))}
                      className="absolute right-3 top-2.5 text-xs text-white/40 hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* TEK BİR "FİLTRELER" DÜĞMESİ VE POPOVER PANELİ */}
                <div className="relative" ref={filterPanelRef}>
                  <button
                    type="button"
                    onClick={() => setFilterPanelOpen((prev) => !prev)}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition cursor-pointer ${
                      activeFiltersCount > 0
                        ? "border-yellow-500/50 bg-yellow-500/20 text-yellow-300 shadow-sm"
                        : "border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
                    }`}
                  >
                    <span>⚙️ Filtreler</span>
                    {activeFiltersCount > 0 && (
                      <span className="rounded-full bg-yellow-400 px-1.5 py-0.2 text-[10px] font-black text-black">
                        {activeFiltersCount}
                      </span>
                    )}
                    <span className="text-[10px] opacity-60">▼</span>
                  </button>

                  {/* FİLTRE PANELİ POPOVER */}
                  {filterPanelOpen && (
                    <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 rounded-2xl border border-white/15 bg-[#141418] p-4 shadow-2xl z-40 text-white animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2.5 mb-3">
                        <span className="text-xs font-black uppercase text-yellow-300">Tablo Filtreleri</span>
                        {activeFiltersCount > 0 && (
                          <button
                            type="button"
                            onClick={handleClearFilters}
                            className="text-[11px] font-bold text-red-300 hover:text-red-200"
                          >
                            Temizle
                          </button>
                        )}
                      </div>

                      <div className="space-y-3">
                        {/* Yönetici */}
                        <div>
                          <label className="text-[11px] font-bold text-white/60 block mb-1">Temsilci / Yönetici</label>
                          <select
                            value={filters.manager}
                            onChange={(e) => setFilters((p) => ({ ...p, manager: e.target.value }))}
                            className="w-full rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                          >
                            <option value="">Tümü ({managers.length} Yönetici)</option>
                            {managers.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>

                        {/* Kademe */}
                        <div>
                          <label className="text-[11px] font-bold text-white/60 block mb-1">Kademe (K1–K10)</label>
                          <select
                            value={filters.tier}
                            onChange={(e) => setFilters((p) => ({ ...p, tier: e.target.value }))}
                            className="w-full rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                          >
                            <option value="">Tümü (Tüm Kademeler)</option>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                              <option key={n} value={n}>Kademe {n}</option>
                            ))}
                          </select>
                        </div>

                        {/* Teslimat Durumu (Eğer teslimat tabıysa) */}
                        {activeTab === "delivery_tracking" && (
                          <div>
                            <label className="text-[11px] font-bold text-white/60 block mb-1">Teslimat Durumu</label>
                            <select
                              value={filters.deliveryStatus}
                              onChange={(e) => setFilters((p) => ({ ...p, deliveryStatus: e.target.value }))}
                              className="w-full rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                            >
                              <option value="">Tümü</option>
                              <option value="Talep Bekleniyor">Talep Bekleniyor (Fiziksel)</option>
                              <option value="Bekliyor">Bekliyor</option>
                              <option value="Hazırlanıyor">Hazırlanıyor</option>
                              <option value="Kargoya Verildi">Kargoya Verildi</option>
                              <option value="Teslim Edildi">Teslim Edildi</option>
                              <option value="İptal">İptal</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sağ: Kayıt Bilgisi */}
              <div className="text-xs font-bold text-white/50">
                {filteredRows.length} / {rows.length} Yayıncı
              </div>
            </div>

            {/* AKTİF FİLTRE ETİKETLERİ (CHIPS) & TEK TEK KALDIRMA */}
            {activeFiltersCount > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-white/10 pt-2.5">
                <span className="text-[11px] text-white/40 font-bold mr-1">Aktif Filtreler:</span>
                {filters.publisher && (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-yellow-500/15 border border-yellow-500/30 px-2 py-0.5 text-[11px] font-bold text-yellow-300">
                    <span>Arama: "{filters.publisher}"</span>
                    <button type="button" onClick={() => setFilters((p) => ({ ...p, publisher: "" }))} className="hover:text-white">✕</button>
                  </span>
                )}
                {filters.manager && (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-cyan-500/15 border border-cyan-500/30 px-2 py-0.5 text-[11px] font-bold text-cyan-200">
                    <span>Yönetici: {filters.manager}</span>
                    <button type="button" onClick={() => setFilters((p) => ({ ...p, manager: "" }))} className="hover:text-white">✕</button>
                  </span>
                )}
                {filters.tier && (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 text-[11px] font-bold text-amber-200">
                    <span>Kademe: K{filters.tier}</span>
                    <button type="button" onClick={() => setFilters((p) => ({ ...p, tier: "" }))} className="hover:text-white">✕</button>
                  </span>
                )}
                {filters.deliveryStatus && (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-purple-500/15 border border-purple-500/30 px-2 py-0.5 text-[11px] font-bold text-purple-200">
                    <span>Teslimat: {filters.deliveryStatus}</span>
                    <button type="button" onClick={() => setFilters((p) => ({ ...p, deliveryStatus: "" }))} className="hover:text-white">✕</button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-lg bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] font-bold text-red-300 hover:text-red-200 hover:bg-red-500/20 transition cursor-pointer"
                  title="Filtreleri temizle (Seçili ayı değiştirmez)"
                >
                  ✕ Filtreleri Temizle
                </button>
              </div>
            )}
          </div>

          {/* 🌟 SEÇİLENLER İÇİN TOPLU İŞLEM YÜZEN ÇUBUĞU */}
          {selectedKeys.size > 0 && (
            <div className="sticky top-4 z-40 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-yellow-500/50 bg-gradient-to-r from-zinc-950 via-zinc-900 to-black p-4 shadow-2xl animate-fadeIn text-white">
              <div className="flex items-center gap-2">
                <span className="flex h-3 w-3 rounded-full bg-yellow-400 animate-ping" />
                <span className="text-xs font-black text-yellow-300">
                  📌 {selectedKeys.size} yayıncı seçildi
                </span>
                <span className="text-[11px] text-zinc-400">
                  (Bu döneme ait seçili yayıncılara toplu işlem uygulayabilirsiniz)
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="green"
                  onClick={handleBulkPublishSelected}
                  disabled={isBulkProcessing}
                >
                  ✓ Seçilenleri Yayınla ({selectedKeys.size})
                </Button>
                <Button
                  variant="red"
                  onClick={handleBulkUnpublishSelected}
                  disabled={isBulkProcessing}
                >
                  ✕ Seçilenleri Yayından Kaldır ({selectedKeys.size})
                </Button>
                <Button
                  onClick={() => setSelectedKeys(new Set())}
                  disabled={isBulkProcessing}
                >
                  Seçimi Temizle
                </Button>
              </div>
            </div>
          )}

          {loading && <Card className="p-5 text-sm text-white/50">Performans ve hak ediş verileri hesaplanıyor...</Card>}
          {error && <Card className="border-red-500/20 bg-red-500/10 p-5 text-sm font-bold text-red-200">{error}</Card>}

          {!loading && !error && (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-[1600px] w-full border-collapse text-left text-xs">
                  <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-[0.12em] text-white/45">
                    <tr>
                      <th className="px-3 py-3.5 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={isAllFilteredSelected}
                          onChange={handleToggleSelectAll}
                          className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-yellow-500 focus:ring-0 cursor-pointer"
                          title="Tümünü Seç / Kaldır"
                        />
                      </th>
                      <th className="px-4 py-3.5">Yayıncı / Hidden ID</th>
                      <th className="px-4 py-3.5">Yönetici</th>
                      <th className="px-4 py-3.5">Kademe</th>
                      <th className="px-4 py-3.5">Aktif Gün</th>
                      <th className="px-4 py-3.5">Yayın Saati</th>
                      <th className="px-4 py-3.5">Aylık Elmas</th>
                      <th className="px-4 py-3.5">Şart Durumu</th>
                      <th className="px-4 py-3.5">Hediye / Prim Tanımı</th>
                      <th className="px-4 py-3.5">Yayın Durumu</th>
                      {activeTab === "delivery_tracking" && <th className="px-4 py-3.5">Teslimat Durumu</th>}
                      {(activeTab === "approved_exceptions" || activeTab === "rejected") && <th className="px-4 py-3.5">Değerlendirme Notu</th>}
                      <th className="px-4 py-3.5">İşlemler (Kişi Bazlı)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {filteredRows.map((row) => {
                      const { eligibility } = row;
                      const isAuto = eligibility.status === "AUTO_ELIGIBLE";
                      const isManual = eligibility.status === "MANUAL_REVIEW";
                      const isApproved = row.evaluation?.decision === "APPROVED";
                      const isRejected = row.evaluation?.decision === "REJECTED";
                      const isPublished = row.evaluation?.publishStatus === "Yayınlandı" || (isAuto && !row.evaluation?.decision);
                      const key = getRowKey(row);
                      const isSelected = selectedKeys.has(key);

                      return (
                        <tr key={key} className={`text-white/75 hover:bg-yellow-500/[0.03] transition ${
                          isSelected ? "bg-yellow-500/[0.08]" : ""
                        }`}>
                          <td className="px-3 py-3.5 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleRow(row)}
                              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-yellow-500 focus:ring-0 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-black text-white">{row.publisherUsername}</div>
                            <div className="text-[10px] text-white/40">{row.publisherName} • ID: {row.hiddenId || "-"}</div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-black text-yellow-300">Kademe {row.currentTier}</div>
                            <div className="text-[10px] text-white/40">{row.tierChange}</div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`font-black ${row.activeDays >= 22 ? "text-emerald-300" : "text-amber-300"}`}>
                              {row.activeDays} Gün
                            </span>
                            <span className="ml-1 text-[10px] text-white/30">/ 22</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`font-black ${row.liveHours >= 80 ? "text-emerald-300" : "text-amber-300"}`}>
                              {row.liveHours} Sa
                            </span>
                            <span className="ml-1 text-[10px] text-white/30">/ 80</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`font-black ${row.diamonds >= 40000 ? "text-yellow-300" : "text-amber-300"}`}>
                              {formatNumber(row.diamonds)}
                            </span>
                            <span className="ml-1 text-[10px] text-white/30">/ 40K</span>
                          </td>
                          <td className="px-4 py-3.5">
                            {isAuto && (
                              <span className="rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[10px] font-black text-emerald-200">
                                ✓ 3/3 Otomatik Hak Kazandı
                              </span>
                            )}
                            {isManual && !row.evaluation?.decision && (
                              <div className="space-y-1">
                                <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-[10px] font-black text-amber-200">
                                  ⚠️ 2/3 İnceleme Bekliyor
                                </span>
                                <div className="text-[10px] text-amber-300/90 font-bold">
                                  {eligibility.missingConditions.map((c) => c.label).join(", ")}
                                </div>
                              </div>
                            )}
                            {isApproved && (
                              <span className="rounded-full border border-cyan-500/40 bg-cyan-500/15 px-2.5 py-1 text-[10px] font-black text-cyan-200">
                                ✓ Yönetici Tarafından Onaylandı
                              </span>
                            )}
                            {isRejected && (
                              <span className="rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-1 text-[10px] font-black text-red-200">
                                ✕ Yönetici Tarafından Reddedildi
                              </span>
                            )}
                            {eligibility.status === "NOT_ELIGIBLE" && !row.evaluation?.decision && (
                              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] text-white/40">
                                Şartlar Sağlanmadı ({eligibility.metCount}/3)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-bold text-white max-w-[200px] truncate">{row.assignedGift}</div>
                          </td>

                          <td className="px-4 py-3.5">
                            {row.evaluation?.publishStatus === "Yayınlandı" ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-black text-emerald-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
                                Canlıda Yayınlandı
                              </span>
                            ) : row.evaluation?.publishStatus === "Yayından Kaldırıldı" ? (
                              <span className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-800/80 px-2.5 py-0.5 text-[10px] font-bold text-zinc-400">
                                Yayından Kaldırıldı
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[10px] text-zinc-400">
                                Taslak / Bekliyor
                              </span>
                            )}
                          </td>

                          {activeTab === "delivery_tracking" && (
                            <td className="px-4 py-3.5">
                              <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black ${
                                row.deliveryStatus === "Teslim Edildi" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" :
                                row.deliveryStatus === "Kargoya Verildi" ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-200" :
                                row.deliveryStatus === "Talep Bekleniyor" ? "border-amber-500/40 bg-amber-500/15 text-amber-200" :
                                "border-white/15 bg-white/5 text-white/60"
                              }`}>
                                {row.deliveryStatus}
                              </span>
                              {row.trackingCode && <div className="text-[10px] text-white/40 mt-0.5">Kargo: {row.trackingCode}</div>}
                            </td>
                          )}

                          {(activeTab === "approved_exceptions" || activeTab === "rejected") && (
                            <td className="px-4 py-3.5 max-w-[240px]">
                              <div className="text-[11px] text-white/80 line-clamp-2">{row.evaluation?.evaluationNote || "-"}</div>
                              <div className="text-[9px] text-white/35 mt-0.5">
                                {row.evaluation?.evaluatedBy} • {row.evaluation?.evaluatedAt ? String(row.evaluation.evaluatedAt).slice(0, 16).replace("T", " ") : ""}
                              </div>
                            </td>
                          )}

                          <td className="px-4 py-3.5">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {/* 🟢/🔴 KİŞİ BAZLI YAYINLA / KALDIR BUTONLARI */}
                              {row.evaluation?.publishStatus === "Yayınlandı" ? (
                                <Button
                                  variant="red"
                                  onClick={() => unpublishSingle(row)}
                                  title="Bu yayıncının ödülünü yayından kaldır"
                                >
                                  🔴 Kaldır
                                </Button>
                              ) : (
                                <Button
                                  variant="green"
                                  onClick={() => publishSingle(row)}
                                  title="Bu yayıncının ödülünü yayıncı ekranında canlı yayınla"
                                >
                                  🟢 Yayınla
                                </Button>
                              )}

                              <Button
                                variant="gold"
                                onClick={() => setCustomAwardRow(row)}
                                title="Ödül metnini özelleştir ve yayınla"
                              >
                                ✏️ Düzenle
                              </Button>

                              {isManual && !row.evaluation?.decision && (
                                <Button onClick={() => setReviewingRow(row)}>
                                  İncele
                                </Button>
                              )}

                              {(isAuto || isApproved) && (
                                <Button onClick={() => setDeliveryEditingRow(row)}>
                                  Teslimat
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {!filteredRows.length && (
                      <tr>
                        <td colSpan={12} className="px-5 py-12 text-center text-sm text-white/40">
                          Bu sekmede gösterilecek yayıncı kaydı bulunmuyor.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Manuel Hediye Ekleme Modalı */}
          <ManualGiftAddModal
            isOpen={isManualModalOpen}
            onClose={() => {
              setIsManualModalOpen(false);
              setManualModalInitialData(null);
            }}
            periodMonth={periodMonth}
            giftMatrix={giftMatrix}
            publisherRows={publisherRows}
            managerRows={managerRows}
            currentAdmin={currentAdmin}
            initialData={manualModalInitialData}
            onSaved={({ message }) => {
              setActionMessage(message);
              refreshEvaluations();
            }}
          />

          {/* Excel / CSV Toplu Hediye Yükleme Modalı */}
          <BulkGiftImportModal
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            periodMonth={periodMonth}
            publisherRows={publisherRows}
            currentAdmin={currentAdmin}
            onSaved={({ message }) => {
              setActionMessage(message);
              refreshEvaluations();
            }}
          />

          {/* Özel Ödül Düzenleme ve Yayınlama Modalı */}
          {customAwardRow && (
            <CustomAwardEditModal
              row={customAwardRow}
              periodMonth={periodMonth}
              onClose={() => setCustomAwardRow(null)}
              onSave={(giftText) => {
                publishSingle(customAwardRow, giftText);
                setCustomAwardRow(null);
              }}
            />
          )}

          {/* Manuel İnceleme Modalı */}
          {reviewingRow && (
            <ManualReviewModal
              row={reviewingRow}
              periodMonth={periodMonth}
              currentAdmin={currentAdmin}
              onClose={() => setReviewingRow(null)}
              onEvaluated={refreshEvaluations}
            />
          )}

          {/* Teslimat Düzenleme Modalı */}
          {deliveryEditingRow && (
            <DeliveryEditModal
              row={deliveryEditingRow}
              periodMonth={periodMonth}
              currentAdmin={currentAdmin}
              onClose={() => setDeliveryEditingRow(null)}
              onSaved={refreshEvaluations}
            />
          )}
        </>
      )}
    </div>
  );
}


// 🎁 ÖZEL ÖDÜL DÜZENLEME VE YAYINLAMA MODALI

// 🌟 MANUEL HEDİYE / PRİM TANIMLAMA MODALI
function ManualGiftAddModal({ isOpen, onClose, periodMonth, giftMatrix = [], publisherRows = [], managerRows = [], currentAdmin = {}, initialData = null, onSaved }) {
  const [targetPeriod, setTargetPeriod] = useState(periodMonth || defaultMonth());
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [isCustomPublisher, setIsCustomPublisher] = useState(false);
  const [customUsername, setCustomUsername] = useState("");
  const [customName, setCustomName] = useState("");
  const [customHiddenId, setCustomHiddenId] = useState("");
  const [managerName, setManagerName] = useState("");
  const [currentTier, setCurrentTier] = useState(1);
  const [giftDetail, setGiftDetail] = useState("50 $ Nakit Prim");
  const [adminNote, setAdminNote] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("Talep Bekleniyor");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const presetGifts = useMemo(() => {
    const fromMatrix = (giftMatrix || []).flatMap((m) => [m.upgradeSingleReward, m.upgradePlusReward, m.protectReward]).filter((g) => g && g !== "-");
    const defaults = [
      "100 Elmas", "250 Elmas", "500 Elmas", "1K Elmas", "1K 250 Elmas", "2K Elmas", "5K Elmas", "10K Elmas",
      "50 $ Nakit Prim", "100 $ Nakit Prim", "200 $ Nakit Prim", "300 $ Nakit Prim", "500 $ Nakit Prim",
      "5.000 TL Nakit Prim", "10.000 TL Nakit Prim", "20.000 TL Nakit Prim",
      "Jet (5K)", "Galaksi (1K)", "Havai Fişek (1,1K)", "Balina (2,1K)", "Kedi (4,3K)",
      "Spor Araba (7K)", "Yıldızlararası (10K)", "Parti Hiç Bitmesin (15K)", "Roket (20K)",
      "Aslan (30K)", "Universe (45K)", "Universe (70K)",
      "iPhone 16 Pro", "50.000 Elmas Primi", "100.000 Elmas Primi"
    ];
    return [...new Set([...defaults, ...fromMatrix])];
  }, [giftMatrix]);

  const uniquePublishers = useMemo(() => deduplicatePublishersByHiddenId(publisherRows || []), [publisherRows]);

  useEffect(() => {
    if (isOpen) {
      setTargetPeriod(periodMonth || defaultMonth());
      if (initialData) {
        const u = initialData.username || initialData.originalUsername || "";
        const found = uniquePublishers.find((p) => (p.hiddenId && p.hiddenId === initialData.hiddenId) || normalizeUsername(p.username) === normalizeUsername(u));
        if (found) {
          setSelectedPublisher(found.hiddenId || found.username);
          setIsCustomPublisher(false);
          setCustomUsername(found.username || "");
          setCustomName(found.fullName || found.name || "");
          setCustomHiddenId(found.hiddenId || "");
          setManagerName(found.managerName || "");
        } else {
          setSelectedPublisher("__NEW__");
          setIsCustomPublisher(true);
          setCustomUsername(u);
          setCustomName(initialData.name || initialData.fullName || u);
          setCustomHiddenId(initialData.hiddenId || "");
          setManagerName(initialData.managerName || "");
        }
        setCurrentTier(initialData.currentTier || initialData.yukselmeKademe || 1);
        setGiftDetail(initialData.giftDetail || "50 $ Nakit Prim");
        setAdminNote(initialData.adminNote || (initialData.statusLabel ? `Aktiflik & Yükselme Hakedişi Dışı (${initialData.statusLabel})` : ""));
      } else {
        setSelectedPublisher("");
        setIsCustomPublisher(false);
        setCustomUsername("");
        setCustomName("");
        setCustomHiddenId("");
        setManagerName("");
        setCurrentTier(1);
        setGiftDetail("50 $ Nakit Prim");
        setAdminNote("");
      }
      setErrorMsg("");
    }
  }, [isOpen, initialData, periodMonth, uniquePublishers]);

  const handlePublisherSelect = (e) => {
    const val = e.target.value;
    setSelectedPublisher(val);
    if (val === "__NEW__") {
      setIsCustomPublisher(true);
      setCustomUsername("");
      setCustomName("");
      setCustomHiddenId("");
      setManagerName("");
    } else {
      setIsCustomPublisher(false);
      const found = uniquePublishers.find((p) => (p.hiddenId && p.hiddenId === val) || normalizeUsername(p.username) === normalizeUsername(val));
      if (found) {
        setCustomUsername(found.username || "");
        setCustomName(found.fullName || found.name || "");
        setCustomHiddenId(found.hiddenId || "");
        setManagerName(found.managerName || "");
      }
    }
  };

  const handleSave = async (publishStatus = "Yayınlandı") => {
    const finalUsername = isCustomPublisher ? customUsername.trim() : (customUsername || selectedPublisher);
    const finalHiddenId = customHiddenId.trim();

    if (!finalUsername && !finalHiddenId) {
      setErrorMsg("Lütfen geçerli bir yayıncı seçin veya kullanıcı adı girin.");
      return;
    }
    if (!giftDetail.trim()) {
      setErrorMsg("Lütfen hediye / prim tanımını girin.");
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");

      const payload = {
        periodMonth: targetPeriod,
        hiddenId: finalHiddenId,
        publisherUsername: finalUsername.replace(/^@/, ""),
        publisherName: customName.trim() || finalUsername,
        managerName: managerName.trim() || "-",
        currentTier: Number(currentTier) || 1,
        giftDetail: giftDetail.trim(),
        publishStatus,
        deliveryStatus,
        adminNote: adminNote.trim(),
        createdBy: currentAdmin?.username || "admin",
      };

      await requestJson(API_GIFT_AWARD_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const identifier = finalHiddenId || normalizeUsername(finalUsername);
      saveStoredEvaluation(targetPeriod, identifier, {
        decision: publishStatus === "Yayınlandı" ? "APPROVED" : "PENDING",
        evaluatedBy: currentAdmin?.username || "admin",
        giftDetail: giftDetail.trim(),
        publishStatus,
        deliveryStatus,
        evaluationNote: adminNote.trim(),
      });

      onSaved?.({
        message: publishStatus === "Yayınlandı" 
          ? `✓ @${finalUsername} için ödül (${giftDetail}) başarıyla kullanıcı ekranına yayınlandı.`
          : `✓ @${finalUsername} için hediye taslak olarak kaydedildi ve aktiflik detaylarına yansıtıldı.`,
        addedRow: {
          username: normalizeUsername(finalUsername),
          originalUsername: finalUsername,
          giftDetail: giftDetail.trim(),
          adminNote: adminNote.trim(),
          publishStatus,
          isManual: true,
          hakEdis: parseFloat(giftDetail.replace(/[^0-9.,]/g, "").replace(",", ".")) || 0
        }
      });
      onClose();
    } catch (err) {
      setErrorMsg(`Kaydetme hatası: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fadeIn">
      <div className="w-full max-w-xl rounded-3xl border border-yellow-500/40 bg-zinc-950 p-6 shadow-2xl text-white space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <span>➕ Manuel Hediye / Özel Prim Tanımla</span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              Hakedişi olmayan veya ek hediye vermek istediğiniz yayıncıya özel ödül tanımlayın.
            </p>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full bg-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition">
            ✕
          </button>
        </div>

        {errorMsg && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-200">
            {errorMsg}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold text-zinc-400 mb-1">Hesaplama Dönemi</label>
            <input
              type="month"
              value={targetPeriod}
              onChange={(e) => setTargetPeriod(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-zinc-400 mb-1">Kademe (K1 - K10)</label>
            <select
              value={currentTier}
              onChange={(e) => setCurrentTier(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>Kademe {n} (K{n})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-zinc-400 mb-1">Yayıncı Seçimi</label>
            <select
              value={selectedPublisher}
              onChange={handlePublisherSelect}
              className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
            >
              <option value="">-- Kayıtlı Yayıncılardan Seçin --</option>
              {uniquePublishers.map((p) => (
                <option key={p.hiddenId || p.username} value={p.hiddenId || p.username}>
                  @{p.username} ({p.fullName || p.name || "-"}) {p.hiddenId ? `• ID: ${p.hiddenId}` : ""}
                </option>
              ))}
              <option value="__NEW__">➕ Listede Olmayan Yeni / Harici Yayıncı Gir</option>
            </select>
          </div>

          {isCustomPublisher && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl border border-yellow-500/20 bg-yellow-500/[0.04] p-3.5">
              <div>
                <label className="block text-[10px] font-bold text-yellow-300 mb-1">Kullanıcı Adı (@)</label>
                <input
                  type="text"
                  value={customUsername}
                  onChange={(e) => setCustomUsername(e.target.value)}
                  placeholder="ornek_yayinci"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs text-white outline-none focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-zinc-400 mb-1">Yayıncı Ad Soyad (Opsiyonel)</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Ad Soyad"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs text-white outline-none focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-zinc-400 mb-1">Hidden ID / TikTok ID</label>
                <input
                  type="text"
                  value={customHiddenId}
                  onChange={(e) => setCustomHiddenId(e.target.value)}
                  placeholder="12345678"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs text-white outline-none focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-zinc-400 mb-1">Temsilci / Yönetici Adı</label>
                <input
                  type="text"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                  placeholder="Yönetici Adı"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs text-white outline-none focus:border-yellow-400"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-yellow-300 mb-1">
              Hediye / Prim Tanımı (Yayıncı panelinde birebir bu metin görünecektir):
            </label>
            <input
              type="text"
              value={giftDetail}
              onChange={(e) => setGiftDetail(e.target.value)}
              placeholder="Örn: 100 $ Nakit Prim, Jet (5K), 5.000 TL Nakit Prim..."
              className="w-full rounded-2xl border border-yellow-500/40 bg-zinc-900 px-4 py-3 text-sm font-bold text-white focus:border-yellow-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-zinc-400 mb-1.5">Hızlı Ödül Seçenekleri:</label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
              {presetGifts.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGiftDetail(g)}
                  className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition ${
                    giftDetail === g
                      ? "border-yellow-400 bg-yellow-500/25 text-yellow-200"
                      : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-zinc-400 mb-1">Teslimat Durumu</label>
              <select
                value={deliveryStatus}
                onChange={(e) => setDeliveryStatus(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              >
                <option value="Talep Bekleniyor">Talep Bekleniyor (Fiziksel)</option>
                <option value="Hazırlanıyor">Hazırlanıyor</option>
                <option value="Kargoya Verildi">Kargoya Verildi</option>
                <option value="Teslim Edildi">Teslim Edildi</option>
                <option value="İptal">İptal</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-400 mb-1">Yönetici Notu (Opsiyonel)</label>
              <input
                type="text"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Örn: Manuel performans primi, özel anlaşma..."
                className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/10 pt-4">
          <Button onClick={onClose} disabled={saving}>
            İptal
          </Button>
          <Button
            onClick={() => handleSave("Taslak")}
            disabled={saving}
            className="border-amber-500/30 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
            title="Yayıncıya görünmez; sadece admin aktiflik tablosuna taslak olarak yansır."
          >
            {saving ? "Kaydediliyor..." : "📋 Sadece Aktiflik Detaylarına Yansıt (Taslak)"}
          </Button>
          <Button
            variant="green"
            onClick={() => handleSave("Yayınlandı")}
            disabled={saving}
            title="Hem veritabanına kaydeder hem de anında yayıncının ekranında gösterir."
          >
            {saving ? "Yayınlanıyor..." : "🚀 Doğrudan Canlıya Yayınla"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 🌟 EXCEL / CSV TOPLU HEDİYE VE TİKTOK HAKEDİŞ YÜKLEME MODALI
function BulkGiftImportModal({ isOpen, onClose, periodMonth, publisherRows = [], currentAdmin = {}, onSaved }) {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [isTikTokWorkbook, setIsTikTokWorkbook] = useState(false);
  const [tikTokStats, setTikTokStats] = useState(null);
  const [parsingError, setParsingError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const downloadSampleTemplate = () => {
    const sampleData = [
      {
        "Kullanıcı Adı": "edoss580",
        "Hidden ID": "71938491023",
        "Dönem (YYYY-MM)": periodMonth || defaultMonth(),
        "Kademe": 8,
        "Hediye / Ödül": "382.31 $ Bonus Katkısı (%50 - 5. Seviye)",
        "Yönetici Notu": "Aktiflik & Yükselme Hakedişi",
        "Yayın Durumu": "Yayınlandı",
      },
      {
        "Kullanıcı Adı": "neslimiv",
        "Hidden ID": "68492041234",
        "Dönem (YYYY-MM)": periodMonth || defaultMonth(),
        "Kademe": 2,
        "Hediye / Ödül": "327.32 $ Bonus Katkısı (%50 - 5. Seviye)",
        "Yönetici Notu": "Eylül Hakedişi",
        "Yayın Durumu": "Yayınlandı",
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hediyeler");
    XLSX.writeFile(wb, `ornek_toplu_hediye_sablonu_${periodMonth || "donem"}.xlsx`);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setParsingError("");
    setParsedRows([]);
    setIsTikTokWorkbook(false);
    setTikTokStats(null);

    const fileName = selectedFile.name.toLowerCase();
    try {
      if (fileName.endsWith(".csv") || fileName.endsWith(".txt")) {
        Papa.parse(selectedFile, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            processParsedRawRows(results.data);
          },
          error: (err) => {
            setParsingError(`CSV okuma hatası: ${err.message}`);
          }
        });
      } else {
        const data = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const hasAktiflik = workbook.SheetNames.some((n) => n.toLowerCase().includes("aktiflik"));
        const hasYukselme = workbook.SheetNames.some((n) => n.toLowerCase().includes("yükselme") || n.toLowerCase().includes("yukselme"));

        if (hasAktiflik && hasYukselme) {
          const { rows: calculatedRows, stats } = parseTikTokActivityWorkbook(workbook, periodMonth);
          setIsTikTokWorkbook(true);
          setTikTokStats(stats);
          const eligible = calculatedRows.filter((r) => r.isEligible);
          const processed = eligible.map((r, index) => ({
            id: `import_tt_${index}_${Date.now()}`,
            index: index + 1,
            publisherUsername: r.originalUsername || r.username,
            hiddenId: "",
            periodMonth: r.periodMonth || periodMonth || defaultMonth(),
            currentTier: r.yukselmeKademe || (r.aktiflikLevel >= 5 ? 5 : 4),
            giftDetail: `${r.hakEdis.toFixed(2)} $ Bonus Katkısı (%${(r.multiplier * 100).toFixed(0)} - ${r.aktiflikLevelRaw})`,
            adminNote: `Tahminî Bonus: ${Number(r.yukselmeBonus).toFixed(2)} $ • Aktiflik: ${r.aktiflikLevelRaw}`,
            publishStatus: "Yayınlandı",
            isValid: true,
            validationError: null
          }));
          setParsedRows(processed);
        } else {
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          processParsedRawRows(json);
        }
      }
    } catch (err) {
      setParsingError(`Dosya ayrıştırma hatası: ${err.message}`);
    }
  };

  const processParsedRawRows = (rawArray) => {
    if (!Array.isArray(rawArray) || !rawArray.length) {
      setParsingError("Dosyada geçerli satır bulunamadı.");
      return;
    }

    const processed = rawArray.map((row, index) => {
      const getVal = (...keys) => {
        for (const k of keys) {
          if (row[k] !== undefined && String(row[k]).trim() !== "") return String(row[k]).trim();
          const foundKey = Object.keys(row).find((rk) => normalizeUsername(rk) === normalizeUsername(k));
          if (foundKey && String(row[foundKey]).trim() !== "") return String(row[foundKey]).trim();
        }
        return "";
      };

      const username = getVal("Kullanıcı Adı", "Kullanici Adi", "username", "publisher_username", "yayinci", "yayıncı", "User Name", "Account").replace(/^@/, "");
      const hiddenId = getVal("Hidden ID", "hidden_id", "ID", "Tiktok ID", "hiddenid");
      const rowPeriod = getVal("Dönem (YYYY-MM)", "Donem", "Period", "period_month", "Ay", "Month") || periodMonth || defaultMonth();
      const tier = parseInt(getVal("Kademe", "current_tier", "Tier", "K"), 10) || 1;
      const gift = getVal("Hediye / Ödül", "Hediye", "Ödül", "Odul", "gift_detail", "Gift", "assignedGift", "Prim", "Ödül Adı");
      const note = getVal("Yönetici Notu", "Yonetici Notu", "admin_note", "Not", "Note", "Açıklama");
      const statusRaw = getVal("Yayın Durumu", "Yayin Durumu", "publish_status", "Durum", "Status");
      const publishStatus = statusRaw.toLowerCase().includes("taslak") ? "Taslak" : (statusRaw.toLowerCase().includes("kaldır") ? "Yayından Kaldırıldı" : "Yayınlandı");

      const isValid = Boolean(username || hiddenId) && Boolean(gift);

      return {
        id: `import_${index}_${Date.now()}`,
        index: index + 1,
        publisherUsername: username,
        hiddenId,
        periodMonth: rowPeriod,
        currentTier: tier,
        giftDetail: gift,
        adminNote: note,
        publishStatus,
        isValid,
        validationError: !isValid ? (!gift ? "Ödül / hediye metni eksik" : "Kullanıcı adı veya Hidden ID eksik") : null
      };
    });

    setParsedRows(processed);
  };

  const handleBulkSubmit = async (overrideStatus = null) => {
    const validRows = parsedRows.filter((r) => r.isValid);
    if (!validRows.length) {
      setParsingError("İçe aktarılacak geçerli satır bulunamadı.");
      return;
    }

    try {
      setIsProcessing(true);
      setParsingError("");

      const finalItems = validRows.map((r) => ({
        ...r,
        publishStatus: overrideStatus || r.publishStatus,
        deliveryStatus: "Talep Bekleniyor",
      }));

      const res = await requestJson(API_GIFT_AWARD_BULK_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          periodMonth: periodMonth || defaultMonth(),
          items: finalItems,
          publishStatus: overrideStatus || "Yayınlandı",
          createdBy: currentAdmin?.username || "admin",
        }),
      });

      finalItems.forEach((r) => {
        const identifier = r.hiddenId || normalizeUsername(r.publisherUsername);
        saveStoredEvaluation(r.periodMonth || periodMonth, identifier, {
          decision: (overrideStatus || r.publishStatus) === "Yayınlandı" ? "APPROVED" : "PENDING",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: r.giftDetail,
          publishStatus: overrideStatus || r.publishStatus,
          deliveryStatus: "Talep Bekleniyor",
          evaluationNote: r.adminNote || "",
        });
      });

      onSaved?.({
        message: res?.message || `✓ Toplam ${validRows.length} adet hediye/ödül kaydı başarıyla sisteme aktarıldı.`,
      });
      onClose();
    } catch (err) {
      setParsingError(`Toplu yükleme hatası: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const validCount = parsedRows.filter((r) => r.isValid).length;
  const invalidCount = parsedRows.length - validCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fadeIn">
      <div className="w-full max-w-4xl rounded-3xl border border-yellow-500/40 bg-zinc-950 p-6 shadow-2xl text-white space-y-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <span>📥 Excel / TikTok Raporundan Toplu Hediye ve Prim Yükleme</span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              TikTok Backstage dosyanızı (Aktiflik + Yükselme) veya kendi hediye tablonuzu yükleyin. Sistem 4. seviyeye %30, 5. seviyeye %50 çarpanını otomatik uygular.
            </p>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full bg-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition">
            ✕
          </button>
        </div>

        {parsingError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-200">
            {parsingError}
          </div>
        )}

        {isTikTokWorkbook && tikTokStats && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div>
              <div className="font-black text-emerald-300 flex items-center gap-1.5">
                <span>✓ TikTok Aktiflik & Yükselme Raporu Otomatik Algılandı</span>
              </div>
              <div className="text-[11px] text-emerald-200/80 mt-0.5">
                Eşleşen {tikTokStats.eligibleCount} yayıncı için 4. Seviye (%30) ve 5. Seviye (%50) kuralı uygulandı.
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs font-black text-yellow-300">
              <span>Toplam Dağıtılacak Hakediş:</span>
              <span className="text-base font-black text-yellow-200">${Number(tikTokStats.totalHakedis).toFixed(2)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 rounded-2xl border-2 border-dashed border-yellow-500/30 bg-yellow-500/[0.02] p-5 text-center hover:border-yellow-400 transition">
            <input
              type="file"
              id="bulkGiftFileInput"
              accept=".xlsx, .xls, .csv, .txt"
              onChange={handleFileChange}
              className="hidden"
            />
            <label htmlFor="bulkGiftFileInput" className="cursor-pointer flex flex-col items-center justify-center gap-2">
              <span className="text-3xl">📁</span>
              <span className="text-xs font-black text-yellow-300">
                {file ? file.name : "Excel (.xlsx, .xls) veya CSV Dosyası Seçin / Sürükleyin"}
              </span>
              <span className="text-[11px] text-zinc-400">
                {file ? `${(file.size / 1024).toFixed(1)} KB yüklendi` : "TikTok Backstage (Aktiflik & Yükselme) veya hediye tablonuzu yükleyebilirsiniz"}
              </span>
            </label>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col justify-between">
            <div>
              <div className="text-xs font-black text-white">Hazır Şablon İle Yükleyin</div>
              <div className="mt-1 text-[11px] text-zinc-400 leading-4">
                Sütun formatına uygun örnek Excel dosyasını indirip yayıncılarınızı ve hediyelerini doldurabilirsiniz.
              </div>
            </div>
            <Button variant="gold" onClick={downloadSampleTemplate} className="w-full mt-3">
              📥 Örnek Şablonu İndir (.xlsx)
            </Button>
          </div>
        </div>

        {parsedRows.length > 0 && (
          <div className="flex-1 overflow-hidden flex flex-col border border-white/10 rounded-2xl bg-black/40">
            <div className="p-3 bg-white/[0.04] border-b border-white/10 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-white">Önizleme: Toplam {parsedRows.length} Kayıt</span>
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                  ✓ {validCount} Geçerli
                </span>
                {invalidCount > 0 && (
                  <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-300">
                    ✕ {invalidCount} Hatalı / Eksik
                  </span>
                )}
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-auto max-h-[260px] flex-1">
              <table className="min-w-[750px] w-full border-collapse text-left text-xs">
                <thead className="bg-white/[0.03] text-[10px] font-black uppercase tracking-wider text-white/50 sticky top-0 bg-zinc-950">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Yayıncı (@)</th>
                    <th className="px-3 py-2">Dönem</th>
                    <th className="px-3 py-2">Kademe / Seviye</th>
                    <th className="px-3 py-2">Hediye / Hakediş</th>
                    <th className="px-3 py-2">Hesaplama Notu</th>
                    <th className="px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {parsedRows.map((r) => (
                    <tr key={r.id} className={r.isValid ? "hover:bg-white/[0.02]" : "bg-red-500/[0.08]"}>
                      <td className="px-3 py-2 text-zinc-500">{r.index}</td>
                      <td className="px-3 py-2 font-bold text-white">
                        {r.publisherUsername ? `@${r.publisherUsername}` : "-"}
                      </td>
                      <td className="px-3 py-2 text-yellow-300/80">{r.periodMonth}</td>
                      <td className="px-3 py-2 font-bold text-zinc-300">K{r.currentTier}</td>
                      <td className="px-3 py-2 font-black text-yellow-200">{r.giftDetail}</td>
                      <td className="px-3 py-2 text-zinc-400 text-[11px] max-w-[200px] truncate">{r.adminNote || "-"}</td>
                      <td className="px-3 py-2">
                        {r.isValid ? (
                          <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                            Geçerli
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-300" title={r.validationError}>
                            {r.validationError}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/10 pt-4">
          <Button onClick={onClose} disabled={isProcessing}>
            İptal
          </Button>
          {parsedRows.length > 0 && (
            <>
              <Button
                onClick={() => handleBulkSubmit("Taslak")}
                disabled={isProcessing || validCount === 0}
                className="border-amber-500/30 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                title="Aktiflik tablosuna taslak olarak yansır; yayıncı ekranında henüz görünmez."
              >
                {isProcessing ? "Yükleniyor..." : `📋 Sadece Aktiflik Detaylarına Yansıt (${validCount} Taslak)`}
              </Button>
              <Button
                variant="green"
                onClick={() => handleBulkSubmit("Yayınlandı")}
                disabled={isProcessing || validCount === 0}
                title="Tüm geçerli yayıncıların ödüllerini anında canlıya yayınlar ve kullanıcı ekranına iletir."
              >
                {isProcessing ? "Yayınlanıyor..." : `🚀 Toplu Canlıya Yayınla (${validCount} Yayıncı)`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 🌟 TİKTOK AKTİFLİK & YÜKSELME AYLIK HAKEDİŞ VE HEDİYE YÖNETİM MODÜLÜ
export function TikTokActivityProgressionContent({
  publisherRows = [],
  managerRows = [],
  currentAdmin = {},
  periodMonth = getLatestUploadedPeriod(),
  setPeriodMonth = () => {},
  onOpenManualModal = () => {},
  onRefreshEvaluations = () => {},
}) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    totalHakedis: 0,
    eligibleCount: 0,
    level5Count: 0,
    level4Count: 0,
    unmatchedCount: 0,
    manualCount: 0,
    totalRows: 0
  });
  const [filterTab, setFilterTab] = useState("eligible");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeFiltersCount = (filterTab !== "all" && filterTab !== "eligible" ? 1 : 0) + (statusFilter !== "all" ? 1 : 0) + (searchQuery.trim() ? 1 : 0);
  const handleClearFilters = () => {
    setFilterTab("all");
    setStatusFilter("all");
    setSearchQuery("");
    setFilterPanelOpen(false);
  };

  const [editingRowId, setEditingRowId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editNote, setEditNote] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [rowActionLoading, setRowActionLoading] = useState(null);
  const [bannerMessage, setBannerMessage] = useState(null);
  const [savedMonths, setSavedMonths] = useState(() => getSavedTikTokReportMonths());
  const [isSyncing, setIsSyncing] = useState(false);

  // 🪙 Dinamik Jeton / Elmas Birim Kuru (Varsayılan: 0.02 $, localStorage ile kalıcı)
  const [jetonDollarRate, setJetonDollarRate] = useState(() => {
    try {
      const stored = localStorage.getItem("WOLF_JETON_DOLLAR_RATE");
      const num = parseFloat(stored);
      return num > 0 ? num : 0.02;
    } catch {
      return 0.02;
    }
  });

  const handleJetonRateChange = (newVal) => {
    const val = parseFloat(newVal);
    if (!isNaN(val) && val > 0) {
      setJetonDollarRate(val);
      try {
        localStorage.setItem("WOLF_JETON_DOLLAR_RATE", String(val));
      } catch (e) {
        console.error(e);
      }
    } else if (newVal === "" || newVal === "0" || newVal === "0.") {
      setJetonDollarRate(newVal);
    }
  };

  const handleResetJetonRate = () => {
    setJetonDollarRate(0.02);
    try {
      localStorage.setItem("WOLF_JETON_DOLLAR_RATE", "0.02");
    } catch (e) {
      console.error(e);
    }
  };

  // Kayıtlı Değerlendirme & Yayın Durumunu Satırlara Entegre Et
  const attachStoredStatus = useCallback((rawRows, currentPeriod) => {
    const stored = loadStoredEvaluations();
    return rawRows.map((r) => {
      const uname = r.originalUsername || r.username;
      const key = `${currentPeriod}:${normalizeUsername(uname)}`;
      const saved = stored[key];
      let pubStatus = r.publishStatus;
      if (saved && saved.publishStatus) {
        pubStatus = saved.publishStatus;
      } else if (!pubStatus || pubStatus === "TASLAK" || pubStatus === "YOK") {
        pubStatus = (r.isEligible || r.isManual || (r.customHakEdis != null && r.customHakEdis > 0)) ? "TASLAK" : "YOK";
      }
      return {
        ...r,
        publishStatus: pubStatus,
        publishedAt: saved?.updatedAt || null,
        deliveryStatus: saved?.deliveryStatus || null,
      };
    });
  }, []);

  // Dönem değiştiğinde veya sayfa yüklendiğinde o ayın kaydedilmiş verisini otomatik yükle
  useEffect(() => {
    const allReports = loadMonthlyTikTokReports();
    const available = Object.keys(allReports).sort().reverse();
    setSavedMonths(available);

    const saved = allReports[periodMonth];
    if (saved && Array.isArray(saved.rows) && saved.rows.length > 0) {
      const rowsWithStatus = attachStoredStatus(saved.rows, periodMonth);
      setRows(rowsWithStatus);
      setStats(saved.stats || buildTikTokReportStats(rowsWithStatus));
      setFile({ name: saved.fileName || `Hesaplama_${periodMonth}.xlsx` });
    } else {
      setRows([]);
      setStats({
        totalHakedis: 0,
        eligibleCount: 0,
        level5Count: 0,
        level4Count: 0,
        unmatchedCount: 0,
        manualCount: 0,
        totalRows: 0
      });
      setFile(null);
    }
  }, [periodMonth, attachStoredStatus]);

  // Sunucudaki (diğer bilgisayarlarda veya API'de kayıtlı) hesaplama raporlarını yükle/eşitle
  useEffect(() => {
    let isMounted = true;
    fetchMonthlyTikTokReportsFromServer().then(({ reports }) => {
      if (!isMounted || !reports) return;
      const available = Object.keys(reports).sort().reverse();
      setSavedMonths(available);
      const saved = reports[periodMonth];
      if (saved && Array.isArray(saved.rows) && saved.rows.length > 0) {
        setRows((prev) => {
          if (!prev || prev.length === 0 || saved.rows.length >= prev.length) {
            const rowsWithStatus = attachStoredStatus(saved.rows, periodMonth);
            setStats(saved.stats || buildTikTokReportStats(rowsWithStatus));
            setFile({ name: saved.fileName || `Hesaplama_${periodMonth}.xlsx` });
            return rowsWithStatus;
          }
          return prev;
        });
      }
    });
    return () => { isMounted = false; };
  }, [periodMonth, attachStoredStatus]);

  const handleManualSync = async () => {
    try {
      setIsSyncing(true);
      const { reports } = await fetchMonthlyTikTokReportsFromServer();
      if (reports) {
        const available = Object.keys(reports).sort().reverse();
        setSavedMonths(available);
        const saved = reports[periodMonth];
        if (saved && Array.isArray(saved.rows) && saved.rows.length > 0) {
          const rowsWithStatus = attachStoredStatus(saved.rows, periodMonth);
          setRows(rowsWithStatus);
          setStats(saved.stats || buildTikTokReportStats(rowsWithStatus));
          setFile({ name: saved.fileName || `Hesaplama_${periodMonth}.xlsx` });
          setBannerMessage({
            type: "success",
            text: `✓ Sunucudan ${periodLabel(periodMonth)} dönemi için ${saved.rows.length} yayıncılık hesaplama verisi başarıyla çekildi.`
          });
        } else {
          setBannerMessage({
            type: "info",
            text: `ℹ Sunucuda ${periodLabel(periodMonth)} dönemi için henüz kayıtlı hesaplama verisi bulunmuyor.`
          });
        }
      }
    } catch (err) {
      setBannerMessage({
        type: "error",
        text: `✕ Sunucuyla eşitlenirken hata oluştu: ${err.message}`
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Dosya Yükleme ve Ayrıştırma (Anında Kalıcı Kaydeder)
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const result = parseTikTokActivityWorkbook(workbook, periodMonth);
      const rowsWithStatus = attachStoredStatus(result.rows, periodMonth);
      setRows(rowsWithStatus);
      setStats(result.stats);

      // Kalıcı Olarak Sakla
      saveMonthlyTikTokReport(periodMonth, {
        fileName: selectedFile.name,
        uploadedAt: new Date().toISOString(),
        rows: rowsWithStatus,
        stats: result.stats,
        uploadedBy: currentAdmin?.username || "admin"
      });
      setSavedMonths(getSavedTikTokReportMonths());

      setBannerMessage({
        type: "success",
        text: `✓ "${selectedFile.name}" dosyası başarıyla yüklendi ve ${periodLabel(periodMonth)} için kalıcı kaydedildi. ${result.stats.eligibleCount} hak kazanan yayıncı hesaplandı ($${result.stats.totalHakedis.toFixed(2)}).`
      });
    } catch (err) {
      setBannerMessage({
        type: "error",
        text: `✕ Dosya okunamadı: ${err.message}`
      });
    }
  };

  // Düzenleme Başlat
  const startEdit = (row) => {
    setEditingRowId(row.id);
    const current = row.customHakEdis != null ? row.customHakEdis : row.hakEdis;
    setEditAmount(String(current));
    setEditNote(row.adminNote || "");
  };

  // Düzenleme Kaydet
  const saveEdit = (rowId) => {
    const num = parseFloat(String(editAmount).replace(",", ".")) || 0;
    const nextRows = rows.map((r) => {
      if (r.id === rowId) {
        return {
          ...r,
          customHakEdis: num,
          adminNote: editNote.trim(),
          isManual: true,
          statusLabel: `Manuel Belirlendi ($${num.toFixed(2)})`
        };
      }
      return r;
    });
    const newStats = buildTikTokReportStats(nextRows);
    setRows(nextRows);
    setStats(newStats);
    setEditingRowId(null);

    // Kaydı güncelle
    saveMonthlyTikTokReport(periodMonth, {
      rows: nextRows,
      stats: newStats,
      updatedBy: currentAdmin?.username || "admin"
    });
  };

  // 🚀 BİREYSEL YAYINLAMA (Tek Yayıncının Hakedişini Canlıya Al)
  const handlePublishSingleRow = async (row) => {
    const finalAmount = row.customHakEdis != null ? row.customHakEdis : row.hakEdis;
    const uname = row.originalUsername || row.username;
    const levelStr = row.aktiflikLevel ? `${row.aktiflikLevelRaw || row.aktiflikLevel + '. Seviye'}` : (row.isManual ? "Özel Prim" : "Standart");
    const monthLabel = periodLabel(periodMonth);

    const coins = dollarsToJeton(finalAmount, jetonDollarRate);
    const pubJetonFormatted = formatPublisherElmas(coins);
    const exactElmasFormatted = formatExactElmas(coins);
    const item = {
      periodMonth,
      publisherUsername: uname,
      hiddenId: "",
      currentTier: row.yukselmeKademe || (row.aktiflikLevel >= 5 ? 5 : 4),
      giftDetail: `${pubJetonFormatted} • ${monthLabel} Hakedişi`,
      adminNote: row.adminNote || `Tahminî Bonus: ${Number(row.yukselmeBonus).toFixed(2)} $ • Çarpan: %${(row.multiplier * 100).toFixed(0)} • Admin: ${exactElmasFormatted} • Yayıncı: ${pubJetonFormatted} (1 Jeton = ${jetonDollarRate} $)`,
      publishStatus: "Yayınlandı",
      deliveryStatus: "Talep Bekleniyor"
    };

    try {
      setRowActionLoading(row.id);
      await requestJson(API_GIFT_AWARD_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item)
      });

      const identifier = normalizeUsername(uname);
      saveStoredEvaluation(periodMonth, identifier, {
        decision: "APPROVED",
        evaluatedBy: currentAdmin?.username || "admin",
        giftDetail: item.giftDetail,
        publishStatus: "Yayınlandı",
        deliveryStatus: "Talep Bekleniyor",
        evaluationNote: item.adminNote || ""
      });

      const updatedRows = rows.map((r) =>
        r.id === row.id
          ? { ...r, publishStatus: "Yayınlandı", publishedAt: new Date().toISOString() }
          : r
      );
      setRows(updatedRows);
      saveMonthlyTikTokReport(periodMonth, {
        rows: updatedRows,
        stats,
        jetonRate: jetonDollarRate,
        updatedBy: currentAdmin?.username || "admin"
      });

      setBannerMessage({
        type: "success",
        text: `✓ @${uname} için ${monthLabel} hakedişi (Admin: ${exactElmasFormatted} / Yayıncı: ${pubJetonFormatted} - $${finalAmount.toFixed(2)}) başarıyla yayınlandı!`
      });
      onRefreshEvaluations?.();
    } catch (err) {
      setBannerMessage({
        type: "error",
        text: `✕ @${uname} yayınlanamadı: ${err.message}`
      });
    } finally {
      setRowActionLoading(null);
    }
  };

  // 🗑️ BİREYSEL YAYINDAN KALDIRMA (Tek Yayıncının Hakedişini İptal Et/Kaldır)
  const handleUnpublishSingleRow = async (row) => {
    const uname = row.originalUsername || row.username;
    const monthLabel = periodLabel(periodMonth);

    const confirm = window.confirm(
      `@${uname} kullanıcısının ${monthLabel} dönemi hakedişini yayından kaldırmak istiyor musunuz?`
    );
    if (!confirm) return;

    try {
      setRowActionLoading(row.id);
      await requestJson(API_GIFT_AWARD_UNPUBLISH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          periodMonth,
          publisherUsername: uname
        })
      });

      const identifier = normalizeUsername(uname);
      saveStoredEvaluation(periodMonth, identifier, {
        decision: "REJECTED",
        evaluatedBy: currentAdmin?.username || "admin",
        publishStatus: "Yayından Kaldırıldı",
        deliveryStatus: "İptal Edildi",
        evaluationNote: "Admin tarafından bireysel olarak yayından kaldırıldı"
      });

      const updatedRows = rows.map((r) =>
        r.id === row.id ? { ...r, publishStatus: "Yayından Kaldırıldı" } : r
      );
      setRows(updatedRows);
      saveMonthlyTikTokReport(periodMonth, {
        rows: updatedRows,
        stats,
        jetonRate: jetonDollarRate,
        updatedBy: currentAdmin?.username || "admin"
      });

      setBannerMessage({
        type: "success",
        text: `✓ @${uname} hakedişi ${monthLabel} dönemi için başarıyla yayından kaldırıldı.`
      });
      onRefreshEvaluations?.();
    } catch (err) {
      setBannerMessage({
        type: "error",
        text: `✕ @${uname} yayından kaldırılamadı: ${err.message}`
      });
    } finally {
      setRowActionLoading(null);
    }
  };

  // ⚡ TOPLU KAYDET VE CANLIYA YAYINLA
  const handleBulkPublish = async () => {
    const targetRows = rows.filter((r) => r.isEligible || r.isManual || (r.customHakEdis != null && r.customHakEdis > 0));
    if (!targetRows.length) {
      alert("Yayınlanacak hakediş kaydı bulunamadı.");
      return;
    }

    const monthLabel = periodLabel(periodMonth);
    const totalCoins = dollarsToJeton(stats.totalHakedis, jetonDollarRate);
    const totalExactFormatted = formatExactElmas(totalCoins);
    const totalPubFormatted = formatPublisherElmas(totalCoins);
    const confirm = window.confirm(
      `${monthLabel} dönemi için toplam ${targetRows.length} yayıncının hakedişini (Admin: ${totalExactFormatted} | Yayıncı: ${totalPubFormatted} - $${stats.totalHakedis.toFixed(2)}) sisteme kaydetmek ve yayıncı panellerine iletmek istiyor musunuz?`
    );
    if (!confirm) return;

    try {
      setIsPublishing(true);
      const items = targetRows.map((r) => {
        const finalAmount = r.customHakEdis != null ? r.customHakEdis : r.hakEdis;
        const levelStr = r.aktiflikLevel ? `${r.aktiflikLevelRaw || r.aktiflikLevel + '. Seviye'}` : (r.isManual ? "Özel Prim" : "Standart");
        const coins = dollarsToJeton(finalAmount, jetonDollarRate);
        const pubJetonFormatted = formatPublisherElmas(coins);
        const exactElmasFormatted = formatExactElmas(coins);
        return {
          periodMonth,
          publisherUsername: r.originalUsername || r.username,
          hiddenId: "",
          currentTier: r.yukselmeKademe || (r.aktiflikLevel >= 5 ? 5 : 4),
          giftDetail: `${pubJetonFormatted} • ${monthLabel} Hakedişi`,
          adminNote: r.adminNote || `Tahminî Bonus: ${Number(r.yukselmeBonus).toFixed(2)} $ • Çarpan: %${(r.multiplier * 100).toFixed(0)} • Admin: ${exactElmasFormatted} • Yayıncı: ${pubJetonFormatted} (1 Jeton = ${jetonDollarRate} $)`,
          publishStatus: "Yayınlandı",
          deliveryStatus: "Talep Bekleniyor"
        };
      });

      await requestJson(API_GIFT_AWARD_BULK_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          periodMonth,
          items,
          publishStatus: "Yayınlandı",
          createdBy: currentAdmin?.username || "admin"
        })
      });

      items.forEach((item) => {
        const identifier = normalizeUsername(item.publisherUsername);
        saveStoredEvaluation(periodMonth, identifier, {
          decision: "APPROVED",
          evaluatedBy: currentAdmin?.username || "admin",
          giftDetail: item.giftDetail,
          publishStatus: "Yayınlandı",
          deliveryStatus: "Talep Bekleniyor",
          evaluationNote: item.adminNote || ""
        });
      });

      const updatedRows = rows.map((r) =>
        targetRows.some((t) => t.id === r.id)
          ? { ...r, publishStatus: "Yayınlandı", publishedAt: new Date().toISOString() }
          : r
      );
      setRows(updatedRows);
      saveMonthlyTikTokReport(periodMonth, {
        rows: updatedRows,
        stats,
        jetonRate: jetonDollarRate,
        updatedBy: currentAdmin?.username || "admin"
      });

      setBannerMessage({
        type: "success",
        text: `✓ Toplam ${targetRows.length} yayıncının ${monthLabel} ödülü (Admin: ${totalExactFormatted} / Yayıncı: ${totalPubFormatted} - $${stats.totalHakedis.toFixed(2)}) başarıyla sisteme aktarıldı ve yayınlandı!`
      });
      onRefreshEvaluations?.();
    } catch (err) {
      setBannerMessage({
        type: "error",
        text: `✕ Kaydetme hatası: ${err.message}`
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // 🗑️ Bu Ayın Kayıtlı Raporunu Sil
  const handleDeleteMonthReport = () => {
    const monthLabel = periodLabel(periodMonth);
    const confirm = window.confirm(
      `${monthLabel} dönemine ait tüm kayıtlı hesaplama verilerini ve yüklenmiş Excel dosyasını silmek istiyor musunuz?`
    );
    if (!confirm) return;

    deleteMonthlyTikTokReport(periodMonth);
    setRows([]);
    setStats({
      totalHakedis: 0,
      eligibleCount: 0,
      level5Count: 0,
      level4Count: 0,
      unmatchedCount: 0,
      manualCount: 0,
      totalRows: 0
    });
    setFile(null);
    setSavedMonths(getSavedTikTokReportMonths());
    setBannerMessage({
      type: "info",
      text: `🗑️ ${monthLabel} dönemi hesaplama verileri temizlendi.`
    });
  };

  // WhatsApp Kopyala
  const handleCopyWhatsApp = () => {
    const text = generateWhatsAppGiftReport(rows, periodMonth, stats, jetonDollarRate);
    navigator.clipboard.writeText(text);
    setBannerMessage({
      type: "success",
      text: "✓ WhatsApp / Telegram hakediş tablosu panoya kopyalandı! İstediğiniz sohbete yapıştırabilirsiniz."
    });
  };

  // Filtreleme
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const q = normalizeUsername(searchQuery);
      if (q && !normalizeUsername(`${r.username} ${r.originalUsername} ${r.adminNote || ""}`).includes(q)) {
        return false;
      }
      const finalAmount = r.customHakEdis != null ? r.customHakEdis : r.hakEdis;
      if (filterTab === "eligible" && !(r.isEligible || r.isManual || finalAmount > 0)) return false;
      if (filterTab === "level5" && !(r.aktiflikLevel >= 5 && (r.isEligible || finalAmount > 0))) return false;
      if (filterTab === "level4" && !(r.aktiflikLevel === 4 && (r.isEligible || finalAmount > 0))) return false;
      if (filterTab === "unmatched" && !(!r.isEligible && !r.isManual && finalAmount <= 0)) return false;
      if (filterTab === "manual" && !(r.isManual || r.customHakEdis != null)) return false;

      if (statusFilter === "published" && r.publishStatus !== "Yayınlandı") return false;
      if (statusFilter === "draft" && r.publishStatus === "Yayınlandı") return false;

      return true;
    });
  }, [rows, searchQuery, filterTab, statusFilter]);

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* 📅 KAYITLI AYLAR HIZLI GEÇİŞ ÇUBUĞU */}
      {savedMonths.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-yellow-500/20 bg-black/40 px-3.5 py-2 backdrop-blur-md">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-black uppercase tracking-wider text-yellow-300/80 mr-1 flex items-center gap-1">
              <span>📅</span>
              <span>Kayıtlı Aylar:</span>
            </span>
            {savedMonths.map((m) => {
              const rep = loadMonthlyTikTokReports()[m];
              const count = rep?.stats?.eligibleCount || 0;
              const isCurrent = periodMonth === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPeriodMonth(m)}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1 text-xs font-bold transition cursor-pointer ${
                    isCurrent
                      ? "border border-yellow-400 bg-yellow-500/30 text-yellow-200 shadow-sm"
                      : "border border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <span>{periodLabel(m)}</span>
                  <span className={`rounded-full px-1.5 py-0.2 text-[10px] font-semibold ${
                    isCurrent ? "bg-yellow-400/30 text-yellow-100" : "bg-white/10 text-white/60"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={handleDeleteMonthReport}
              className="text-[10px] font-bold text-red-400 hover:text-red-300 border border-red-500/20 bg-red-500/10 px-2 py-1 rounded-lg transition cursor-pointer"
              title="Bu ayın kayıtlı hesaplama verilerini siler"
            >
              🗑️ {periodLabel(periodMonth)} Sil
            </button>
          )}
        </div>
      )}

      {/* 🚀 İŞLEM & BİLGİLENDİRME ÇUBUĞU (Tekrarsız, Sadeleştirilmiş) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-transparent p-3.5 backdrop-blur-md">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-yellow-300">
              🔥 TikTok Aktiflik & Yükselme Hakedişleri
            </span>
            <span className="rounded-full bg-yellow-500/20 border border-yellow-500/30 px-2 py-0.5 text-[10px] font-black text-yellow-200">
              {periodLabel(periodMonth)}
            </span>
          </div>
          <p className="text-[11px] text-white/60 mt-0.5">
            Aktiflik 4. Seviye için %30, 5. Seviye ve üzeri için %50 yükselme bonusu hesaplanır.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="green"
            onClick={handleBulkPublish}
            disabled={isPublishing || stats.eligibleCount === 0}
            title={`Hesaplanan tüm hakedişleri sisteme kaydeder ve ${periodLabel(periodMonth)} için yayıncı panellerine iletir`}
          >
            {isPublishing ? "Yayınlanıyor..." : `💾 Tümünü Yayınla (${periodLabel(periodMonth)} - ${stats.eligibleCount})`}
          </Button>
          <Button
            onClick={() => exportTikTokActivityCalculationToExcel(rows, periodMonth, stats, jetonDollarRate)}
            disabled={!rows.length}
            title="Hesaplanan listeyi detaylı Excel (.xlsx) olarak indirin"
          >
            📊 Excel İndir (.xlsx)
          </Button>
          <Button
            onClick={handleCopyWhatsApp}
            disabled={!rows.length}
            className="border-emerald-500/30 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 font-bold"
            title="Yayıncı gruplarına atabileceğiniz hazır WhatsApp listesini kopyalar"
          >
            📋 WhatsApp Listesi
          </Button>
        </div>
      </div>

      {bannerMessage && (
        <div className={`rounded-2xl border px-4 py-3 text-xs font-bold flex items-center justify-between animate-fadeIn ${
          bannerMessage.type === "error"
            ? "border-red-500/30 bg-red-500/10 text-red-200"
            : (bannerMessage.type === "info"
              ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200")
        }`}>
          <span>{bannerMessage.text}</span>
          <button type="button" onClick={() => setBannerMessage(null)} className="text-white/60 hover:text-white cursor-pointer">✕</button>
        </div>
      )}

      {/* 6 Metric Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="rounded-2xl border border-yellow-500/40 bg-yellow-500/10 p-3.5 relative overflow-hidden">
          <div className="text-[10px] font-black uppercase tracking-wider text-yellow-300">Toplam Hakediş</div>
          <div className="mt-1 text-xl font-black text-yellow-200">${stats.totalHakedis.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</div>
          <div className="mt-0.5 text-xs font-black text-amber-300">
            💎 {formatExactElmas(dollarsToJeton(stats.totalHakedis, jetonDollarRate))}
          </div>
          <div className="text-[10px] text-zinc-400 font-semibold mt-0.5">
            Yayıncıda: <span className="text-amber-200 font-bold">{formatPublisherElmas(dollarsToJeton(stats.totalHakedis, jetonDollarRate))}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Hak Kazananlar</div>
          <div className="mt-1 text-xl font-black text-emerald-200">{stats.eligibleCount} Yayıncı</div>
          <div className="text-[10px] text-emerald-300/70 mt-0.5">Eşleşen & Şartı Sağlayan</div>
        </div>

        <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-cyan-300">5. Seviye (%50)</div>
          <div className="mt-1 text-xl font-black text-cyan-200">{stats.level5Count} Yayıncı</div>
          <div className="text-[10px] text-cyan-300/70 mt-0.5">%50 Çarpan Kazandı</div>
        </div>

        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-blue-300">4. Seviye (%30)</div>
          <div className="mt-1 text-xl font-black text-blue-200">{stats.level4Count} Yayıncı</div>
          <div className="text-[10px] text-blue-300/70 mt-0.5">%30 Çarpan Kazandı</div>
        </div>

        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-red-300">Liste Dışı / Elenen</div>
          <div className="mt-1 text-xl font-black text-red-200">{stats.unmatchedCount} Yayıncı</div>
          <div className="text-[10px] text-red-300/70 mt-0.5">Tek Sayfada Olan / &lt;4. Sev.</div>
        </div>

        <div className="rounded-2xl border border-purple-500/30 bg-purple-500/10 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-purple-300">Manuel Eklenenler</div>
          <div className="mt-1 text-xl font-black text-purple-200">{stats.manualCount} Yayıncı</div>
          <div className="text-[10px] text-purple-300/70 mt-0.5">Özel Prim Tanımlanan</div>
        </div>
      </div>

      {/* SADELEŞTİRİLMİŞ TOOLBAR: ARAMA, TEK "FİLTRELER" DÜĞMESİ, JETON KURU & DOSYA YÜKLEME */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d10] p-3.5 shadow-md">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5 flex-1">
            {/* Arama Input'u */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <span className="absolute left-3 top-2.5 text-xs text-white/30">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Yayıncı kullanıcı adı ara (@)..."
                className="w-full rounded-xl border border-white/10 bg-black/60 pl-8 pr-8 py-2 text-xs text-white placeholder-white/30 outline-none focus:border-yellow-500/40"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-2.5 text-xs text-white/40 hover:text-white cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>

            {/* TEK BİR "FİLTRELER" DÜĞMESİ VE POPOVER PANELİ */}
            <div className="relative" ref={filterPanelRef}>
              <button
                type="button"
                onClick={() => setFilterPanelOpen((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition cursor-pointer ${
                  activeFiltersCount > 0
                    ? "border-yellow-500/50 bg-yellow-500/20 text-yellow-300 shadow-sm"
                    : "border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
                }`}
              >
                <span>⚙️ Filtreler</span>
                {activeFiltersCount > 0 && (
                  <span className="rounded-full bg-yellow-400 px-1.5 py-0.2 text-[10px] font-black text-black">
                    {activeFiltersCount}
                  </span>
                )}
                <span className="text-[10px] opacity-60">▼</span>
              </button>

              {/* FİLTRE POPOVER PANELİ */}
              {filterPanelOpen && (
                <div className="absolute left-0 mt-2 w-72 rounded-2xl border border-white/15 bg-[#141418] p-4 shadow-2xl z-40 text-white animate-fadeIn">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2.5 mb-3">
                    <span className="text-xs font-black uppercase text-yellow-300">Hesaplama Filtreleri</span>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-[11px] font-bold text-red-300 hover:text-red-200 cursor-pointer"
                      >
                        Temizle
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {/* Hak Ediş / Uygunluk Kriteri */}
                    <div>
                      <label className="text-[11px] font-bold text-white/60 block mb-1">Uygunluk Kriteri</label>
                      <select
                        value={filterTab}
                        onChange={(e) => setFilterTab(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                      >
                        <option value="all">Tümü ({rows.length} Yayıncı)</option>
                        <option value="eligible">🎯 Hak Kazananlar ({stats.eligibleCount})</option>
                        <option value="level5">🌟 5. Seviye (%50 - {stats.level5Count})</option>
                        <option value="level4">⭐ 4. Seviye (%30 - {stats.level4Count})</option>
                        <option value="unmatched">⚠️ Liste Dışı / Elenenler ({stats.unmatchedCount})</option>
                        <option value="manual">➕ Manuel Eklenenler ({stats.manualCount})</option>
                      </select>
                    </div>

                    {/* Yayın Durumu */}
                    <div>
                      <label className="text-[11px] font-bold text-white/60 block mb-1">Yayın Durumu</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                      >
                        <option value="all">Tümü</option>
                        <option value="published">🟢 Yayınlandı</option>
                        <option value="draft">⏳ Taslak (Yayınlanmadı)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dinamik Jeton / Elmas Birim Kuru (Kompakt) */}
            <div className="flex items-center gap-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 shadow-sm">
              <span className="text-xs">🪙</span>
              <span className="text-[10px] font-bold text-amber-300 uppercase tracking-wide">Kur:</span>
              <div className="flex items-center gap-1 bg-black/60 rounded px-1.5 py-0.5 border border-amber-400/30">
                <input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={jetonDollarRate}
                  onChange={(e) => handleJetonRateChange(e.target.value)}
                  className="w-12 bg-transparent text-center font-black text-xs text-amber-300 outline-none"
                  title="1 Jeton / Elmas değerinin dolar ($) karşılığı"
                />
                <span className="text-[10px] font-black text-amber-400">$</span>
              </div>
              {Number(jetonDollarRate) !== 0.02 && (
                <button
                  type="button"
                  onClick={handleResetJetonRate}
                  title="Varsayılan 0.02 $ kuruna geri dön"
                  className="rounded bg-white/10 px-1 py-0.5 text-[9px] font-bold text-zinc-300 hover:text-white transition cursor-pointer"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </div>

          {/* Sağ: Dosya Yükleme & Sunucudan Yenile */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <input
                type="file"
                id="tiktokActivityFileInput"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <label
                htmlFor="tiktokActivityFileInput"
                className="cursor-pointer inline-flex items-center gap-1.5 rounded-xl border border-yellow-500/40 bg-yellow-500/15 px-3 py-2 text-xs font-bold text-yellow-300 hover:bg-yellow-500/25 transition shadow-sm"
              >
                <span>📁</span>
                <span>{file ? file.name : "Excel Yükle (.xlsx)"}</span>
              </label>
            </div>

            <button
              type="button"
              onClick={handleManualSync}
              disabled={isSyncing}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-zinc-200 hover:text-white hover:bg-white/10 transition cursor-pointer"
              title="Sunucudaki kayıtlı raporları kontrol et ve eşitle"
            >
              <span className={isSyncing ? "animate-spin inline-block" : ""}>🔄</span>
              <span>{isSyncing ? "Eşitleniyor..." : "Sunucudan Yenile"}</span>
            </button>
          </div>
        </div>

        {/* AKTİF FİLTRE ETİKETLERİ (CHIPS) & TEK TEK KALDIRMA */}
        {activeFiltersCount > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-white/10 pt-2.5">
            <span className="text-[11px] text-white/40 font-bold mr-1">Aktif Filtreler:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 rounded-lg bg-yellow-500/15 border border-yellow-500/30 px-2 py-0.5 text-[11px] font-bold text-yellow-300">
                <span>Arama: "@{searchQuery}"</span>
                <button type="button" onClick={() => setSearchQuery("")} className="hover:text-white cursor-pointer">✕</button>
              </span>
            )}
            {filterTab !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-bold text-emerald-200">
                <span>
                  Kriter: {
                    filterTab === "eligible" ? "Hak Kazananlar" :
                    filterTab === "level5" ? "5. Seviye (%50)" :
                    filterTab === "level4" ? "4. Seviye (%30)" :
                    filterTab === "unmatched" ? "Liste Dışı" :
                    filterTab === "manual" ? "Manuel Eklenen" : filterTab
                  }
                </span>
                <button type="button" onClick={() => setFilterTab("all")} className="hover:text-white cursor-pointer">✕</button>
              </span>
            )}
            {statusFilter !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-lg bg-cyan-500/15 border border-cyan-500/30 px-2 py-0.5 text-[11px] font-bold text-cyan-200">
                <span>Durum: {statusFilter === "published" ? "Yayınlandı" : "Taslak"}</span>
                <button type="button" onClick={() => setStatusFilter("all")} className="hover:text-white cursor-pointer">✕</button>
              </span>
            )}
            <button
              type="button"
              onClick={handleClearFilters}
              className="rounded-lg bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] font-bold text-red-300 hover:text-red-200 hover:bg-red-500/20 transition cursor-pointer"
              title="Filtreleri temizle (Seçili dönemi değiştirmez)"
            >
              ✕ Filtreleri Temizle
            </button>
          </div>
        )}
      </div>

      {/* Hakediş Listesi Tablosu */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1050px] w-full border-collapse text-left text-xs">
            <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-wider text-white/50 border-b border-white/10">
              <tr>
                <th className="px-4 py-3.5 w-12">#</th>
                <th className="px-4 py-3.5">Yayıncı (@)</th>
                <th className="px-4 py-3.5">
                  <div className="flex items-center gap-1.5">
                    <span>Aktiflik Seviyesi & Oran</span>
                    <button
                      type="button"
                      onClick={() => setFilterTab((prev) => prev === "level5" ? "level4" : (prev === "level4" ? "all" : "level5"))}
                      className="text-[11px] text-yellow-400 hover:text-white cursor-pointer"
                      title="5. Seviye ve 4. Seviye arasında hızlı filtrele"
                    >
                      ⚡
                    </button>
                  </div>
                </th>
                <th className="px-4 py-3.5">Yükselme Kademe & Oran</th>
                <th className="px-4 py-3.5">Tahminî Yükselme Bonusu</th>
                <th className="px-4 py-3.5">Uygulanan Çarpan</th>
                <th className="px-4 py-3.5">Net Hakediş Tutarı ($)</th>
                <th className="px-4 py-3.5">Jeton / Elmas Hakedişi</th>
                <th className="px-4 py-3.5">
                  <div className="flex items-center gap-1.5">
                    <span>Yayın Durumu</span>
                    <button
                      type="button"
                      onClick={() => setStatusFilter((prev) => prev === "published" ? "draft" : (prev === "draft" ? "all" : "published"))}
                      className="text-[11px] text-yellow-400 hover:text-white cursor-pointer"
                      title="Yayınlandı ve Taslak arasında hızlı filtrele"
                    >
                      ⚡
                    </button>
                  </div>
                </th>
                <th className="px-4 py-3.5 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-zinc-400">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <span className="text-4xl">📁</span>
                      <span className="font-black text-white text-base">
                        {periodLabel(periodMonth)} Dönemi İçin Kayıt Bulunamadı
                      </span>
                      <span className="text-xs text-zinc-400 max-w-md leading-relaxed">
                        {rows.length === 0 
                          ? `Bu aya (${periodLabel(periodMonth)}) ait TikTok Aktiflik ve Yükselme Excel dosyasını yukarıdaki butondan yükleyebilirsiniz.`
                          : "Arama veya filtre kriterlerinize uygun kayıt bulunamadı."}
                      </span>
                      {rows.length === 0 && savedMonths.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                          <span className="text-xs text-zinc-500">Yüklü aylara hızlı geçiş yapın:</span>
                          {savedMonths.map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setPeriodMonth(m)}
                              className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3 py-1 text-xs font-bold text-yellow-300 hover:bg-yellow-500/20 transition"
                            >
                              {periodLabel(m)} ➔
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, idx) => {
                  const finalAmount = r.customHakEdis != null ? r.customHakEdis : r.hakEdis;
                  const isEditing = editingRowId === r.id;
                  const isPublished = r.publishStatus === "Yayınlandı";
                  const isRevoked = r.publishStatus === "Yayından Kaldırıldı";
                  const hasEligibleAmount = finalAmount > 0 || r.isEligible || r.isManual;
                  const monthLabel = periodLabel(periodMonth);

                  return (
                    <tr key={r.id} className="hover:bg-white/[0.02] transition">
                      <td className="px-4 py-3.5 text-zinc-500 font-mono">{idx + 1}</td>
                      <td className="px-4 py-3.5 font-bold text-white">
                        <div className="flex items-center gap-1.5">
                          <span className="text-yellow-300">@</span>
                          <span>{r.originalUsername || r.username}</span>
                        </div>
                        {r.adminNote && (
                          <div className="text-[10px] text-zinc-400 italic mt-0.5">Not: {r.adminNote}</div>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {r.inAktiflik ? (
                          <div>
                            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-black ${
                              r.aktiflikLevel >= 5
                                ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/30"
                                : (r.aktiflikLevel === 4
                                  ? "bg-blue-500/20 text-blue-200 border border-blue-500/30"
                                  : "bg-zinc-800 text-zinc-300")
                            }`}>
                              {r.aktiflikLevelRaw || `${r.aktiflikLevel}. Seviye`}
                            </span>
                            <div className="text-[10px] text-zinc-400 mt-0.5">
                              Oran: %{(r.aktiflikRate * 100).toFixed(2)} • {Number(r.aktiflikDiamonds || 0).toLocaleString("tr-TR")} Elmas
                            </div>
                          </div>
                        ) : (
                          <span className="rounded-md bg-red-500/10 border border-red-500/20 px-2 py-0.5 text-[10px] text-red-300">
                            Aktiflikte Yok
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {r.inYukselme ? (
                          <div>
                            <span className="font-bold text-zinc-200">{r.yukselmeKademeRaw || `Kademe ${r.yukselmeKademe}`}</span>
                            <div className="text-[10px] text-zinc-400 mt-0.5">Oran: %{(r.yukselmeRate * 100).toFixed(2)}</div>
                          </div>
                        ) : (
                          <span className="rounded-md bg-red-500/10 border border-red-500/20 px-2 py-0.5 text-[10px] text-red-300">
                            Yükselmede Yok
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-zinc-300">
                        {r.inYukselme ? `${Number(r.yukselmeBonus).toFixed(2)} $` : "-"}
                      </td>
                      <td className="px-4 py-3.5">
                        {r.multiplier > 0 ? (
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${
                            r.multiplier === 0.50
                              ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                              : "bg-blue-500/20 text-blue-200 border border-blue-500/40"
                          }`}>
                            %{(r.multiplier * 100).toFixed(0)}
                          </span>
                        ) : (
                          <span className="text-zinc-500 font-bold">%0</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {isEditing ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                step="0.01"
                                value={editAmount}
                                onChange={(e) => setEditAmount(e.target.value)}
                                className="w-24 rounded-lg border border-yellow-400 bg-zinc-900 px-2 py-1 text-xs text-white font-bold"
                              />
                              <button
                                type="button"
                                onClick={() => saveEdit(r.id)}
                                className="rounded-lg bg-emerald-500 px-2 py-1 text-[10px] font-black text-black hover:bg-emerald-400"
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingRowId(null)}
                                className="rounded-lg bg-zinc-700 px-2 py-1 text-[10px] text-white hover:bg-zinc-600"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="text-[10px] text-amber-300 font-mono">
                              💎 {formatExactElmas(dollarsToJeton(editAmount, jetonDollarRate))} (Yayıncı: {formatPublisherElmas(dollarsToJeton(editAmount, jetonDollarRate))})
                            </div>
                          </div>
                        ) : (
                          <div className="font-black text-yellow-300 text-sm flex items-center gap-1">
                            <span>${Number(finalAmount).toFixed(2)}</span>
                            {r.isManual && (
                              <span className="rounded bg-purple-500/20 text-purple-300 px-1 text-[9px] font-bold">Özel</span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* 💎 JETON / ELMAS HAKEDİŞİ ALANI (Admin Tam Değer, Yayıncı K Görünümü) */}
                      <td className="px-4 py-3.5">
                        {finalAmount > 0 ? (
                          (() => {
                            const coins = dollarsToJeton(finalAmount, jetonDollarRate);
                            return (
                              <div className="flex flex-col">
                                <div className="inline-flex items-center gap-1.5 font-black text-amber-300 text-sm whitespace-nowrap">
                                  <span>💎</span>
                                  <span>{formatExactElmas(coins)}</span>
                                </div>
                                <div className="text-[10px] text-zinc-400 font-semibold whitespace-nowrap">
                                  Yayıncıda: <span className="text-amber-200 font-bold">{formatPublisherElmas(coins)}</span>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-zinc-600 font-bold">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {isPublished ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 px-2.5 py-1 text-[10px] font-black text-emerald-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>🟢 Yayınlandı ({monthLabel})</span>
                          </span>
                        ) : isRevoked ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 border border-red-500/40 px-2.5 py-1 text-[10px] font-black text-red-300">
                            <span>🔴</span>
                            <span>Yayından Kaldırıldı</span>
                          </span>
                        ) : hasEligibleAmount ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-500/40 px-2.5 py-1 text-[10px] font-bold text-amber-200">
                            <span>⏳</span>
                            <span>Taslak (Yayın Bekliyor)</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-zinc-800 border border-white/10 px-2 py-0.5 text-[10px] font-semibold text-zinc-400" title={r.statusLabel}>
                            {r.status === "ONLY_YUKSELME" ? "Aktiflikte Yok" : (r.status === "ONLY_AKTIFLIK" ? "Yükselmede Yok" : "Seviye Yetersiz")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* 🚀 Bireysel Yayınla / 🗑️ Yayından Kaldır Butonu */}
                          {isPublished ? (
                            <button
                              type="button"
                              onClick={() => handleUnpublishSingleRow(r)}
                              disabled={rowActionLoading === r.id}
                              className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 bg-red-500/15 px-2.5 py-1 text-[11px] font-bold text-red-300 hover:bg-red-500/25 transition active:scale-95"
                              title={`@${r.originalUsername || r.username} hakedişini ${monthLabel} dönemi için yayından kaldırır`}
                            >
                              <span>🗑️</span>
                              <span>{rowActionLoading === r.id ? "İşleniyor..." : "Yayından Kaldır"}</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handlePublishSingleRow(r)}
                              disabled={rowActionLoading === r.id || !hasEligibleAmount}
                              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-black transition active:scale-95 ${
                                hasEligibleAmount
                                  ? "border border-emerald-500/50 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 shadow-sm"
                                  : "border border-white/10 bg-white/5 text-zinc-500 cursor-not-allowed"
                              }`}
                              title={`@${r.originalUsername || r.username} için ${monthLabel} hakedişini yayınlar`}
                            >
                              <span>🚀</span>
                              <span>{rowActionLoading === r.id ? "Yayınlanıyor..." : `Yayınla (${monthLabel})`}</span>
                            </button>
                          )}

                          {!isEditing && (
                            <button
                              type="button"
                              onClick={() => startEdit(r)}
                              className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-zinc-300 hover:text-white hover:bg-white/10 transition"
                              title="Tutar veya notu düzenle"
                            >
                              ✏️ Düzenle
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onOpenManualModal?.({
                              username: r.originalUsername || r.username,
                              name: r.originalUsername || r.username,
                              hiddenId: "",
                              currentTier: r.yukselmeKademe || 1,
                              giftDetail: finalAmount > 0 ? `${finalAmount.toFixed(2)} $ Nakit Prim` : "50 $ Nakit Prim",
                              statusLabel: r.statusLabel
                            })}
                            className="rounded-lg border border-yellow-500/30 bg-yellow-500/15 px-2 py-1 text-[11px] font-bold text-yellow-300 hover:bg-yellow-500/25 transition"
                            title="Bu yayıncıya özel manuel hediye / prim tanımla"
                          >
                            ➕ Manuel Prim
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


function CustomAwardEditModal({ row, periodMonth, onClose, onSave }) {
  const [giftText, setGiftText] = useState(row?.assignedGift || "Jet (5K)");

  const presetGifts = [
    "Jet (5K)",
    "Galaksi (1K)",
    "Havai Fişek (1,1K)",
    "Balina (2,1K)",
    "Kedi (4,3K)",
    "Spor Araba (7K)",
    "Yıldızlararası (10K)",
    "Parti Hiç Bitmesin (15K)",
    "Roket (20K)",
    "Aslan (30K)",
    "Universe (45K)",
    "Universe (70K)",
    "5.000 TL Nakit Prim",
    "10.000 TL Nakit Prim",
    "iPhone 16 Pro",
    "Yayın Ekipmanı Paketi"
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn">
      <div className="w-full max-w-lg rounded-3xl border border-yellow-500/40 bg-zinc-950 p-6 shadow-2xl text-white space-y-4">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <span>🎁 Ödülü Düzenle ve Kişiye Özel Yayınla</span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              @{row?.publisherUsername} ({row?.publisherName}) • {formatGiftPeriodLabel(periodMonth)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full bg-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <label className="block text-xs font-bold text-yellow-300">
            Ödül / Prim Tanımı (Yayıncı panelinde birebir bu metin görünecektir):
          </label>
          <input
            type="text"
            value={giftText}
            onChange={(e) => setGiftText(e.target.value)}
            placeholder="Örn: Jet (5K) veya 5.000 TL Prim..."
            className="w-full rounded-2xl border border-yellow-500/40 bg-zinc-900 px-4 py-3 text-sm font-bold text-white focus:border-yellow-400 focus:outline-none"
          />

          <div>
            <label className="block text-[11px] font-bold text-zinc-400 mb-1.5">
              Hızlı Seçenekler:
            </label>
            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
              {presetGifts.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGiftText(g)}
                  className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition ${
                    giftText === g
                      ? "border-yellow-400 bg-yellow-500/20 text-yellow-200"
                      : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-4">
          <Button onClick={onClose}>
            İptal
          </Button>
          <Button
            variant="green"
            onClick={() => onSave(giftText)}
          >
            ✓ Kaydet ve Bu Yayıncı İçin Yayınla
          </Button>
        </div>
      </div>
    </div>
  );
}


export { GiftDispatchManagementContent, PublisherBalancesContent };
export const GiftAwardsContent = GiftDispatchManagementContent;

export function GiftMonthlyTrackingContent({ publisherRows = [], managerRows = [], currentAdmin = {} }) {
  return <GiftActiveDetailsContent publisherRows={publisherRows} managerRows={managerRows} currentAdmin={currentAdmin} initialTab="delivery_tracking" />;
}

// 📊 AYLIK HAKEDİŞ VE DAĞITIM RAPORLARI MERKEZİ (Aylık Toplu ve Detay Tablolar)
export function GiftReportsContent({ currentAdmin = {}, setActiveKey = () => {} }) {
  const [reports, setReports] = useState(() => loadMonthlyTikTokReports());
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const months = Object.keys(loadMonthlyTikTokReports()).sort().reverse();
    return months[0] || defaultMonth();
  });
  const [filterTab, setFilterTab] = useState("eligible");
  const [searchQuery, setSearchQuery] = useState("");
  const [bannerMessage, setBannerMessage] = useState(null);

  const activeJetonRate = useMemo(() => {
    try {
      const stored = parseFloat(localStorage.getItem("WOLF_JETON_DOLLAR_RATE"));
      return stored > 0 ? stored : 0.02;
    } catch {
      return 0.02;
    }
  }, []);

  const monthsList = useMemo(() => {
    return Object.keys(reports).sort().reverse();
  }, [reports]);

  useEffect(() => {
    let isMounted = true;
    fetchMonthlyTikTokReportsFromServer().then(({ reports: sReports }) => {
      if (!isMounted || !sReports) return;
      if (Object.keys(sReports).length > 0) {
        setReports(sReports);
        const available = Object.keys(sReports).sort().reverse();
        if (!available.includes(selectedMonth) && available.length > 0) {
          setSelectedMonth(available[0]);
        }
      }
    });
    return () => { isMounted = false; };
  }, []);

  const refreshReports = async () => {
    const { reports: sReports } = await fetchMonthlyTikTokReportsFromServer();
    const data = sReports || loadMonthlyTikTokReports();
    setReports(data);
    const available = Object.keys(data).sort().reverse();
    if (!available.includes(selectedMonth) && available.length > 0) {
      setSelectedMonth(available[0]);
    }
  };

  const currentReport = reports[selectedMonth] || null;
  const currentRows = currentReport?.rows || [];
  const currentStats = currentReport?.stats || {
    totalHakedis: 0,
    eligibleCount: 0,
    level5Count: 0,
    level4Count: 0,
    unmatchedCount: 0,
    manualCount: 0,
    totalRows: 0
  };

  // Toplam İstatistikler (Tüm Ayların Kümülatifi)
  const totalStats = useMemo(() => {
    let totalPaid = 0;
    let totalEligible = 0;
    Object.values(reports).forEach((rep) => {
      totalPaid += rep?.stats?.totalHakedis || 0;
      totalEligible += rep?.stats?.eligibleCount || 0;
    });
    return {
      totalMonths: Object.keys(reports).length,
      totalPaid,
      totalEligible
    };
  }, [reports]);

  // WhatsApp Kopyala
  const handleCopyWhatsApp = (monthKey) => {
    const rep = reports[monthKey];
    if (!rep || !rep.rows) return;
    const text = generateWhatsAppGiftReport(rep.rows, monthKey, rep.stats || {}, rep?.jetonRate || activeJetonRate);
    navigator.clipboard.writeText(text);
    setBannerMessage({
      type: "success",
      text: `✓ ${periodLabel(monthKey)} dönemi WhatsApp hakediş metni panoya kopyalandı!`
    });
  };

  // Filtrelenmiş satırlar
  const filteredRows = useMemo(() => {
    return currentRows.filter((r) => {
      const q = normalizeUsername(searchQuery);
      if (q && !normalizeUsername(`${r.username} ${r.originalUsername} ${r.adminNote || ""}`).includes(q)) {
        return false;
      }
      const finalAmount = r.customHakEdis != null ? r.customHakEdis : r.hakEdis;
      if (filterTab === "eligible") return r.isEligible || r.isManual || finalAmount > 0;
      if (filterTab === "level5") return r.aktiflikLevel >= 5 && (r.isEligible || finalAmount > 0);
      if (filterTab === "level4") return r.aktiflikLevel === 4 && (r.isEligible || finalAmount > 0);
      if (filterTab === "unmatched") return !r.isEligible && !r.isManual && finalAmount <= 0;
      if (filterTab === "manual") return r.isManual || r.customHakEdis != null;
      return true;
    });
  }, [currentRows, searchQuery, filterTab]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Üst Başlık */}
      <div className="rounded-3xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 via-black to-black p-6 backdrop-blur-md">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/20 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-yellow-300">
              <span>📊 WOLF MEDIA HAKEDİŞ & PRİM RAPORLARI</span>
            </div>
            <h2 className="mt-2 text-2xl font-black text-white">Aylık Toplu Raporlar ve Hakediş Arşivi</h2>
            <p className="mt-1 text-xs text-white/60 max-w-2xl leading-relaxed">
              Yüklenen tüm aylık TikTok Aktiflik & Yükselme hesaplamaları burada kalıcı olarak arşivlenir. Ay bazında toplu dağıtım özetlerini görebilir, Excel ve WhatsApp çıktılarını alabilirsiniz.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={refreshReports} title="Raporları yenile">
              🔄 Raporları Yenile
            </Button>
            <Button
              variant="gold"
              onClick={() => setActiveKey?.("gift_active_details")}
              title="Yeni ay hakedişi hesaplamak için hesaplama motoruna git"
            >
              🚀 Yeni Hesaplama Yap
            </Button>
          </div>
        </div>
      </div>

      {bannerMessage && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-xs font-bold text-emerald-200 flex items-center justify-between">
          <span>{bannerMessage.text}</span>
          <button type="button" onClick={() => setBannerMessage(null)} className="text-white/60 hover:text-white">✕</button>
        </div>
      )}

      {/* 3 Genel Kümülatif Metrik Kartı */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-yellow-500/40 bg-yellow-500/10 p-5">
          <div className="text-[10px] font-black uppercase tracking-wider text-yellow-300">Toplam Dağıtılan Hakediş</div>
          <div className="mt-2 text-3xl font-black text-yellow-200">
            ${totalStats.totalPaid.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-yellow-300/70 mt-1">Tüm Kayıtlı Ayların Toplamı</div>
        </div>

        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <div className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Toplam Hak Kazanan Yayıncı</div>
          <div className="mt-2 text-3xl font-black text-emerald-200">
            {totalStats.totalEligible} Yayıncı
          </div>
          <div className="text-xs text-emerald-300/70 mt-1">Dönemler Boyunca Bonus Alan</div>
        </div>

        <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-5">
          <div className="text-[10px] font-black uppercase tracking-wider text-cyan-300">Kayıtlı Dönem Sayısı</div>
          <div className="mt-2 text-3xl font-black text-cyan-200">
            {totalStats.totalMonths} Ay
          </div>
          <div className="text-xs text-cyan-300/70 mt-1">Arşivlenen Hesaplama Dönemi</div>
        </div>
      </div>

      {/* 📋 1. TABLO: AYLIK TOPLU HAKEDİŞ VE DAĞITIM ÖZETİ */}
      <Card className="overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base font-black text-white flex items-center gap-2">
              <span>📅</span>
              <span>Aylık Toplu Hakediş Özeti</span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">Sisteme yüklenmiş tüm ayların toplu karşılaştırma ve çıktı tablosu</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse text-left text-xs">
            <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-wider text-white/50 border-b border-white/10">
              <tr>
                <th className="px-4 py-3.5 w-12">#</th>
                <th className="px-4 py-3.5">Dönem / Ay</th>
                <th className="px-4 py-3.5">Yüklenen Dosya & Tarih</th>
                <th className="px-4 py-3.5">Toplam Dağıtılan ($)</th>
                <th className="px-4 py-3.5">Hak Kazananlar</th>
                <th className="px-4 py-3.5">5. Seviye (%50)</th>
                <th className="px-4 py-3.5">4. Seviye (%30)</th>
                <th className="px-4 py-3.5 text-right">Rapor Çıktıları & İnceleme</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {monthsList.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-zinc-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="text-3xl">📁</span>
                      <span className="font-bold text-white">Henüz Kayıtlı Bir Ay Raporu Bulunmuyor</span>
                      <span className="text-xs text-zinc-500">
                        "Yayıncı Aktiflik Detayları" modülünden TikTok Excel dosyasını yüklediğinizde bu tabloda otomatik listelenecektir.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                monthsList.map((mKey, idx) => {
                  const rep = reports[mKey];
                  const st = rep?.stats || {};
                  const isSelected = selectedMonth === mKey;
                  const uploadDateStr = rep?.uploadedAt ? new Date(rep.uploadedAt).toLocaleString("tr-TR") : "-";

                  return (
                    <tr
                      key={mKey}
                      className={`transition cursor-pointer ${
                        isSelected ? "bg-yellow-500/[0.08] ring-1 ring-inset ring-yellow-500/30" : "hover:bg-white/[0.02]"
                      }`}
                      onClick={() => setSelectedMonth(mKey)}
                    >
                      <td className="px-4 py-3.5 text-zinc-500 font-mono">{idx + 1}</td>
                      <td className="px-4 py-3.5 font-bold text-white">
                        <div className="flex items-center gap-2">
                          <span className="text-base">📅</span>
                          <span className="text-sm font-black text-yellow-300">{periodLabel(mKey)}</span>
                          {isSelected && (
                            <span className="rounded-full bg-yellow-400/20 text-yellow-300 border border-yellow-500/30 px-2 py-0.2 text-[9px] font-black">
                              SEÇİLİ
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-zinc-200">{rep?.fileName || `Hesaplama_${mKey}.xlsx`}</div>
                        <div className="text-[10px] text-zinc-400 mt-0.5">{uploadDateStr}</div>
                      </td>
                      <td className="px-4 py-3.5 font-black text-yellow-300 text-sm">
                        ${Number(st.totalHakedis || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-emerald-300">
                        {st.eligibleCount || 0} Yayıncı
                      </td>
                      <td className="px-4 py-3.5 text-cyan-300 font-semibold">
                        {st.level5Count || 0} Yayıncı
                      </td>
                      <td className="px-4 py-3.5 text-blue-300 font-semibold">
                        {st.level4Count || 0} Yayıncı
                      </td>
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => exportTikTokActivityCalculationToExcel(rep.rows || [], mKey, st)}
                            className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-bold text-zinc-200 hover:text-white hover:bg-white/10 transition flex items-center gap-1"
                            title="Excel (.xlsx) olarak indir"
                          >
                            <span>📊</span>
                            <span>Excel</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopyWhatsApp(mKey)}
                            className="rounded-lg border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-200 hover:bg-emerald-500/25 transition flex items-center gap-1"
                            title="WhatsApp rapor metnini panoya kopyala"
                          >
                            <span>📋</span>
                            <span>WhatsApp</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedMonth(mKey)}
                            className={`rounded-lg px-2.5 py-1 text-[11px] font-black transition flex items-center gap-1 ${
                              isSelected
                                ? "bg-yellow-400 text-black shadow-md"
                                : "border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20"
                            }`}
                          >
                            <span>👁️</span>
                            <span>{isSelected ? "İnceleniyor" : "İncele"}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 🔍 2. TABLO: SEÇİLİ AYIN DETAYLI YAYINCI LİSTESİ */}
      {currentReport && (
        <div className="space-y-4 pt-4 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-yellow-300">Detaylı Yayıncı Raporu</div>
              <h3 className="text-xl font-black text-white flex items-center gap-2">
                <span>{periodLabel(selectedMonth)} Ayı Yayıncı Hakediş Detayları</span>
                <span className="text-xs font-bold text-zinc-400">({currentRows.length} Yayıncı)</span>
              </h3>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Yayıncı kullanıcı adı ara (@)..."
                className="w-64 rounded-xl border border-white/10 bg-zinc-900 px-3 py-1.5 text-xs text-white outline-none focus:border-yellow-400"
              />
              <Button
                onClick={() => exportTikTokActivityCalculationToExcel(currentRows, selectedMonth, currentStats, activeJetonRate)}
                className="text-xs"
              >
                📊 {periodLabel(selectedMonth)} Excel İndir
              </Button>
            </div>
          </div>

          {/* Filtre Butonları */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFilterTab("eligible")}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition flex items-center gap-1.5 ${
                filterTab === "eligible"
                  ? "bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 shadow-sm"
                  : "bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              <span>🎯 Hak Kazananlar</span>
              <span className="rounded-full bg-emerald-500/30 px-1.5 py-0.2 text-[10px] text-emerald-100">{currentStats.eligibleCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("level5")}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition flex items-center gap-1.5 ${
                filterTab === "level5"
                  ? "bg-cyan-500/25 text-cyan-200 border border-cyan-500/40 shadow-sm"
                  : "bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              <span>🌟 5. Seviye (%50)</span>
              <span className="rounded-full bg-cyan-500/30 px-1.5 py-0.2 text-[10px] text-cyan-100">{currentStats.level5Count}</span>
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("level4")}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition flex items-center gap-1.5 ${
                filterTab === "level4"
                  ? "bg-blue-500/25 text-blue-200 border border-blue-500/40 shadow-sm"
                  : "bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              <span>⭐ 4. Seviye (%30)</span>
              <span className="rounded-full bg-blue-500/30 px-1.5 py-0.2 text-[10px] text-blue-100">{currentStats.level4Count}</span>
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("unmatched")}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition flex items-center gap-1.5 ${
                filterTab === "unmatched"
                  ? "bg-red-500/25 text-red-200 border border-red-500/40 shadow-sm"
                  : "bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              <span>⚠️ Liste Dışı / Elenenler</span>
              <span className="rounded-full bg-red-500/30 px-1.5 py-0.2 text-[10px] text-red-100">{currentStats.unmatchedCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("all")}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition flex items-center gap-1.5 ${
                filterTab === "all"
                  ? "bg-yellow-500/25 text-yellow-200 border border-yellow-500/40 shadow-sm"
                  : "bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              <span>Tümü ({currentRows.length})</span>
            </button>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-[1000px] w-full border-collapse text-left text-xs">
                <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-wider text-white/50 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3.5 w-12">#</th>
                    <th className="px-4 py-3.5">Yayıncı (@)</th>
                    <th className="px-4 py-3.5">Aktiflik Seviyesi & Oran</th>
                    <th className="px-4 py-3.5">Yükselme Kademe & Oran</th>
                    <th className="px-4 py-3.5">Tahminî Yükselme Bonusu</th>
                    <th className="px-4 py-3.5">Uygulanan Çarpan</th>
                    <th className="px-4 py-3.5">Net Hakediş Tutarı ($)</th>
                    <th className="px-4 py-3.5">Jeton / Elmas Hakedişi</th>
                    <th className="px-4 py-3.5 text-right">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-zinc-400">
                        Arama veya filtre kriterlerine uygun yayıncı bulunamadı.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r, idx) => {
                      const finalAmount = r.customHakEdis != null ? r.customHakEdis : r.hakEdis;
                      const isPublished = r.publishStatus === "Yayınlandı";

                      return (
                        <tr key={r.id} className="hover:bg-white/[0.02] transition">
                          <td className="px-4 py-3 text-zinc-500 font-mono">{idx + 1}</td>
                          <td className="px-4 py-3 font-bold text-white">
                            <span className="text-yellow-300">@</span>
                            <span>{r.originalUsername || r.username}</span>
                          </td>
                          <td className="px-4 py-3">
                            {r.inAktiflik ? (
                              <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-black ${
                                r.aktiflikLevel >= 5
                                  ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/30"
                                  : (r.aktiflikLevel === 4
                                    ? "bg-blue-500/20 text-blue-200 border border-blue-500/30"
                                    : "bg-zinc-800 text-zinc-300")
                              }`}>
                                {r.aktiflikLevelRaw || `${r.aktiflikLevel}. Seviye`}
                              </span>
                            ) : (
                              <span className="text-red-400 text-[10px]">Aktiflikte Yok</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {r.inYukselme ? (
                              <span className="font-semibold text-zinc-200">{r.yukselmeKademeRaw || `Kademe ${r.yukselmeKademe}`}</span>
                            ) : (
                              <span className="text-red-400 text-[10px]">Yükselmede Yok</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-semibold text-zinc-300">
                            {r.inYukselme ? `${Number(r.yukselmeBonus).toFixed(2)} $` : "-"}
                          </td>
                          <td className="px-4 py-3 font-black">
                            {r.multiplier > 0 ? `%${(r.multiplier * 100).toFixed(0)}` : "%0"}
                          </td>
                          <td className="px-4 py-3 font-black text-yellow-300 text-sm">
                            ${Number(finalAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 font-black text-amber-300 text-xs whitespace-nowrap">
                            {finalAmount > 0 ? (
                              (() => {
                                const coins = dollarsToJeton(finalAmount, activeJetonRate);
                                return (
                                  <div className="flex flex-col">
                                    <div className="inline-flex items-center gap-1 font-black text-amber-300 text-xs whitespace-nowrap">
                                      <span>💎</span>
                                      <span>{formatExactElmas(coins)}</span>
                                    </div>
                                    <div className="text-[10px] text-zinc-400 font-semibold whitespace-nowrap">
                                      Yayıncıda: <span className="text-amber-200 font-bold">{formatPublisherElmas(coins)}</span>
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <span className="text-zinc-600">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isPublished ? (
                              <span className="inline-flex items-center rounded-full bg-emerald-500/20 border border-emerald-500/30 px-2.5 py-0.5 text-[10px] font-bold text-emerald-300">
                                🟢 Yayınlandı
                              </span>
                            ) : finalAmount > 0 ? (
                              <span className="inline-flex items-center rounded-full bg-amber-500/20 border border-amber-500/30 px-2.5 py-0.5 text-[10px] font-bold text-amber-300">
                                ⏳ Taslak
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-zinc-800 border border-white/10 px-2 py-0.5 text-[10px] text-zinc-400">
                                Şartı Sağlamadı
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
